




import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Container, Grid,
    FormControl, InputLabel, Select, MenuItem, TextField,
    RadioGroup, FormControlLabel, Radio, Button, Card, CardContent,
    CircularProgress, Alert, Chip, Fade, Grow, Dialog, DialogTitle,
    DialogContent, DialogActions, LinearProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, IconButton, Slide
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/Upload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import RuleIcon from '@mui/icons-material/Rule';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Header from '../Header'; // Assuming Header component exists
import * as XLSX from 'xlsx';
// Assuming local_url is defined in a constants file
import { local_signage, local_url, web_hook } from '../Const'; 
// const local_url = "https://your-api-url.com"; // Placeholder for local_url

// --- Enhanced Style & Color Definitions ---
const colors = {
    primary: '#4f46e5', // Modern indigo
    primaryLight: '#6366f1',
    primaryDark: '#3730a3',
    primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    
    success: '#10b981', // Modern emerald
    successLight: '#34d399',
    successDark: '#059669',
    successGradient: 'linear-gradient(135deg, #a7f3d0 0%, #10b981 100%)',
    
    error: '#ef4444', // Modern red
    errorLight: '#f87171',
    errorDark: '#dc2626',
    
    warning: '#f59e0b', // Modern amber
    warningLight: '#fbbf24',
    warningDark: '#d97706',
    
    info: '#3b82f6', // Modern blue
    infoLight: '#60a5fa',
    infoDark: '#2563eb',
    
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',
    
    border: '#e2e8f0',
    borderLight: '#f1f5f9',
    borderDark: '#cbd5e1',
    
    background: '#fafafa',
    backgroundCard: '#ffffff',
    backgroundGray: '#f8fafc',
    backgroundAccent: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    
    white: '#ffffff',
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    shadowXl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
};

// --- Enhanced Glassmorphism Style Object ---
const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid rgba(255, 255, 255, 0.3)`,
    boxShadow: colors.shadowXl,
    borderRadius: '16px'
};

// --- Modern Card Style ---
const modernCardStyle = {
    background: colors.backgroundCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '16px',
    boxShadow: colors.shadow,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: colors.shadowLg,
        borderColor: colors.borderDark
    }
};

// --- Gradient Button Styles ---
const gradientButtonStyle = {
    background: colors.primaryGradient,
    color: colors.white,
    border: 'none',
    borderRadius: '12px',
    fontWeight: 600,
    textTransform: 'none',
    padding: '10px 24px',
    boxShadow: colors.shadowMd,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: colors.shadowLg,
        background: `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
    },
    '&:active': {
        transform: 'translateY(0px)'
    }
};

// --- Reusable UI Components (Moved Outside Main Component) ---

const CustomerSearchModal = ({ open, onClose, searchResults, selectedCustomers, handleCustomerSelect, handleConfirmCustomerSelection }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{sx: {borderRadius: '12px', ...glassmorphismStyle}}}>
        <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                <PersonSearchIcon sx={{mr: 1, color: colors.primary}}/> Select Customers (Multiple Selection)
            </Box>
            <Chip 
                label={`${selectedCustomers.length} Selected`} 
                color="primary" 
                size="small" 
                sx={{backgroundColor: colors.success, color: 'white'}}
            />
        </DialogTitle>
        <DialogContent>
             <Grid container spacing={1.5} sx={{mt: 1}}>
                {searchResults.map((customer, index) => {
                    const customerId = customer.custID || customer.customerCode;
                    const isSelected = selectedCustomers.some(c => (c.custID || c.customerCode) === customerId);
                    return (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card onClick={(event) => handleCustomerSelect(customer, event)} sx={{ border: isSelected ? `2px solid ${colors.success}` : `1px solid ${colors.border}`, cursor: 'pointer', position: 'relative', transition: 'all 0.2s ease', '&:hover': {transform: 'translateY(-2px)', boxShadow: 3}, backgroundColor: 'rgba(255,255,255,0.8)' }}>
                                {isSelected && <CheckCircleIcon sx={{ position: 'absolute', top: 6, right: 6, color: colors.success, fontSize: '18px', backgroundColor: 'white', borderRadius: '50%' }} />}
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Typography variant="subtitle2" noWrap sx={{ display: 'flex', alignItems: 'center' }}><PersonOutlineIcon sx={{ mr: 0.5, fontSize: '16px' }} />{customer.customerName}</Typography>
                                    <Chip icon={<PhoneIcon sx={{fontSize: '14px'}} />} label={customer.mobile || 'N/A'} size="small" sx={{ mt: 1, fontSize: '0.75rem', backgroundColor: '#e0e0e0' }} />
                                    <Chip label={`ID: ${customer.custID || customer.customerCode || 'N/A'}`} size="small" sx={{ mt: 1, ml: 1, fontSize: '0.75rem', backgroundColor: '#004D00', color: 'white' }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={handleConfirmCustomerSelection} sx={{backgroundColor: colors.primary, '&:hover': {backgroundColor: colors.primaryDark}}} disabled={selectedCustomers.length === 0}>Confirm Selection ({selectedCustomers.length})</Button></DialogActions>
    </Dialog>
);

const MappingProgressDialog = ({ open, loadingMapping, onClose, mappingProgress, mappingStatus, totalCustomersToUpload, currentCustomerUploadIndex, uploadResults }) => (
     <Dialog open={open} onClose={loadingMapping ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: '12px', ...glassmorphismStyle}}}>
        <DialogTitle sx={{display: 'flex', alignItems: 'center'}}><UploadIcon sx={{mr: 1, color: colors.primary}}/> Processing Upload</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            {totalCustomersToUpload > 1 && (
                <Box sx={{mb: 2}}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Uploading for Customer {currentCustomerUploadIndex + 1} of {totalCustomersToUpload}
                    </Typography>
                    <LinearProgress 
                        variant="determinate" 
                        value={(currentCustomerUploadIndex / totalCustomersToUpload) * 100} 
                        sx={{ 
                            height: 6, 
                            borderRadius: 3, 
                            mb: 1,
                            '& .MuiLinearProgress-bar': { backgroundColor: colors.info } 
                        }} 
                    />
                </Box>
            )}
            
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Current Upload: {mappingProgress.toFixed(0)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={mappingProgress} sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: colors.success } }} />
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: colors.textSecondary }}>{mappingStatus}</Typography>
            </Box>

            {uploadResults && uploadResults.length > 0 && (
                <Box sx={{ maxHeight: 150, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 2, p: 1 }}>
                    {uploadResults.map((result, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {result.success ? (
                                <CheckCircleIcon sx={{ color: colors.success, fontSize: '16px', mr: 1 }} />
                            ) : (
                                <CloseIcon sx={{ color: colors.error, fontSize: '16px', mr: 1 }} />
                            )}
                            <Typography variant="caption" sx={{ color: result.success ? colors.success : colors.error }}>
                                {result.customerName}: {result.success ? 'Success' : result.error}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </DialogContent>
        <DialogActions><Button onClick={onClose} disabled={loadingMapping}>{loadingMapping ? 'Processing...' : 'Close'}</Button></DialogActions>
    </Dialog>
);

const ValidationProgressDialog = ({ open, validationProgress, validationStatus, totalCustomers, onClose }) => (
    <Dialog open={open} onClose={undefined} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: '12px', ...glassmorphismStyle}}}>
        <DialogTitle sx={{display: 'flex', alignItems: 'center'}}>
            <RuleIcon sx={{mr: 1, color: colors.primary}}/> Validating Excel Data
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <CircularProgress size={40} sx={{ color: colors.primary }} />
            </Box>
            
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Validation Progress: {validationProgress.toFixed(0)}%
                    </Typography>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={validationProgress} 
                    sx={{ 
                        height: 8, 
                        borderRadius: 4, 
                        '& .MuiLinearProgress-bar': { backgroundColor: colors.primary } 
                    }} 
                />
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: colors.textSecondary }}>
                    {validationStatus}
                </Typography>
            </Box>

            {totalCustomers > 0 && (
                <Box sx={{ 
                    backgroundColor: colors.backgroundGray, 
                    borderRadius: 2, 
                    p: 2, 
                    border: `1px solid ${colors.border}` 
                }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: colors.textPrimary }}>
                        Selected Customers: {totalCustomers}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        Progress calculated as: {totalCustomers > 0 ? `${(100/totalCustomers).toFixed(1)}% per customer` : '100% total'}
                    </Typography>
                </Box>
            )}
        </DialogContent>
    </Dialog>
);

const DeleteProgressDialog = ({ open, deleteProgress, deleteStatus, totalCustomers, deleteResults, onClose }) => (
    <Dialog open={open} onClose={undefined} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: '12px', ...glassmorphismStyle}}}>
        <DialogTitle sx={{display: 'flex', alignItems: 'center'}}>
            <CloseIcon sx={{mr: 1, color: colors.error}}/> Deleting Customer Schemes
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <CircularProgress size={40} sx={{ color: colors.error }} />
            </Box>
            
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Delete Progress: {deleteProgress.toFixed(0)}%
                    </Typography>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={deleteProgress} 
                    sx={{ 
                        height: 8, 
                        borderRadius: 4, 
                        '& .MuiLinearProgress-bar': { backgroundColor: colors.error } 
                    }} 
                />
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: colors.textSecondary }}>
                    {deleteStatus}
                </Typography>
            </Box>

            {totalCustomers > 1 && (
                <Box sx={{ 
                    backgroundColor: colors.backgroundGray, 
                    borderRadius: 2, 
                    p: 2, 
                    border: `1px solid ${colors.border}`,
                    mb: 2
                }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: colors.textPrimary }}>
                        Deleting schemes for {totalCustomers} customers
                    </Typography>
                </Box>
            )}

            {deleteResults && deleteResults.length > 0 && (
                <Box sx={{ maxHeight: 150, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 2, p: 1 }}>
                    {deleteResults.map((result, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {result.success ? (
                                <CheckCircleIcon sx={{ color: colors.success, fontSize: '16px', mr: 1 }} />
                            ) : (
                                <CloseIcon sx={{ color: colors.error, fontSize: '16px', mr: 1 }} />
                            )}
                            <Typography variant="caption" sx={{ color: result.success ? colors.success : colors.error }}>
                                {result.customerName}: {result.success ? 'Deleted' : result.error}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </DialogContent>
        <DialogActions><Button onClick={onClose} disabled={deleteProgress > 0 && deleteProgress < 100}>Close</Button></DialogActions>
    </Dialog>
);

const CustomerMappingConfirmDialog = ({ open, onClose, customers, areaName, onConfirm }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: '12px', ...glassmorphismStyle}}}>
        <DialogTitle sx={{display: 'flex', alignItems: 'center'}}>
            <PersonSearchIcon sx={{mr: 1, color: colors.warning}}/> 
            Confirm Customer Mapping
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, color: colors.textPrimary }}>
                Are you sure you want to map {customers.length > 1 ? 'these customers' : 'this customer'} as HORECA in <strong>{areaName}</strong> AREA?
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 2, p: 2 }}>
                {customers.map((customer, index) => (
                    <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: colors.backgroundLight, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                            {customer.customerName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                            ID: {customer.custID || customer.CustCode || customer.CustomerId || customer.custCode}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Typography variant="caption" sx={{ mt: 2, color: colors.textSecondary, display: 'block' }}>
                This action will add {customers.length > 1 ? 'these customers' : 'this customer'} to the HORECA rebate scheme for the selected area.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} sx={{ color: colors.textSecondary }}>
                Cancel
            </Button>
            <Button 
                variant="contained" 
                onClick={onConfirm} 
                sx={{ 
                    backgroundColor: colors.warning, 
                    '&:hover': { backgroundColor: colors.warningDark } 
                }}
            >
                Yes, Map Customer{customers.length > 1 ? 's' : ''}
            </Button>
        </DialogActions>
    </Dialog>
);

const MasterCustomerMappingDialog = ({ open, onClose, unmappedCustomers, onMapCustomer, onBulkMapping }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{sx: {borderRadius: '12px', ...glassmorphismStyle}}}>
        <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                <PersonAddIcon sx={{mr: 1, color: colors.warning}}/>
                Customer Mapping Required
            </Box>
            <Button onClick={onBulkMapping} variant="contained" size="small" sx={{borderRadius: '8px', background: colors.primaryGradient}}>
                Map All
            </Button>
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2" sx={{mb: 2, color: colors.textSecondary}}>
                The following customers are not mapped to any area and need to be mapped before upload:
            </Typography>
            <TableContainer sx={{...glassmorphismStyle, borderRadius: '8px'}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight: 600, color: colors.textPrimary}}>Customer ID</TableCell>
                            <TableCell sx={{fontWeight: 600, color: colors.textPrimary}}>Target Area ID</TableCell>
                            <TableCell sx={{fontWeight: 600, color: colors.textPrimary}}>Row Numbers</TableCell>
                            <TableCell sx={{fontWeight: 600, color: colors.textPrimary}}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {unmappedCustomers.map((customer, index) => (
                            <TableRow key={index}>
                                <TableCell>{customer.CustomerID}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={customer.AreaID}
                                        size="small"
                                        sx={{
                                            background: colors.primaryGradient,
                                            color: colors.white,
                                            fontWeight: 500
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{color: colors.textSecondary}}>
                                        {customer.rowNumbers.join(', ')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => onMapCustomer(customer)}
                                        sx={{
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            borderColor: colors.primary,
                                            color: colors.primary,
                                            '&:hover': {
                                                borderColor: colors.primaryDark,
                                                backgroundColor: `${colors.primary}10`
                                            }
                                        }}
                                    >
                                        Map Now
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogContent>
        <DialogActions sx={{p: 2}}>
            <Button onClick={onClose} variant="outlined" sx={{borderRadius: '8px'}}>Close</Button>
        </DialogActions>
    </Dialog>
);

const MapSchemeModal = ({
    open,
    onClose,
    customer,
    availableSchemes,
    selectedScheme,
    onSchemeSelect,
    schemeRateData,
    loadingSchemes,
    loadingRateData,
    onMapScheme
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{sx: {borderRadius: '12px', ...glassmorphismStyle}}}>
        <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                <AssignmentIcon sx={{mr: 1, color: colors.primary}}/>
                Map Rebate Scheme
            </Box>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Customer: {customer?.CustomerName}
            </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, color: colors.textPrimary }}>
                        Select Rebate Scheme
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Rebate Scheme</InputLabel>
                        <Select
                            value={selectedScheme}
                            label="Rebate Scheme"
                            onChange={(e) => onSchemeSelect(e.target.value)}
                            disabled={loadingSchemes}
                        >
                            {availableSchemes.map((scheme) => (
                                <MenuItem key={scheme.RebateSchemeId} value={scheme.RebateSchemeId}>
                                    {scheme.RebateSchemeName}
                                </MenuItem>
                            ))}
                        </Select>
                        {loadingSchemes && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, color: colors.textPrimary }}>
                        Scheme Rate Details
                    </Typography>
                    {loadingRateData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : selectedScheme && schemeRateData.length > 0 ? (
                        <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Area Name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Item Name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Rate
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: colors.primary, color: 'white' }}>
                                            Qualify Qty
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {schemeRateData.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{row.AreaName}</TableCell>
                                            <TableCell>{row.ItemName}</TableCell>
                                            <TableCell>{row.Rate}</TableCell>
                                            <TableCell>{row.QualifyQty}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : selectedScheme && schemeRateData.length === 0 ? (
                        <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', mt: 4 }}>
                            No rate data available for selected scheme
                        </Typography>
                    ) : (
                        <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', mt: 4 }}>
                            Select a scheme to view rate details
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>
                Cancel
            </Button>
            <Button
                variant="contained"
                disabled={!selectedScheme}
                onClick={onMapScheme}
                sx={{
                    backgroundColor: colors.primary,
                    '&:hover': {
                        backgroundColor: colors.primaryDark
                    }
                }}
            >
                Map Scheme
            </Button>
        </DialogActions>
    </Dialog>
);

const StepBox = ({ title, icon, children }) => (
    <Box sx={{
        ...modernCardStyle,
        p: 1.5, 
        height: '100%',
        background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundGray} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: colors.primaryGradient,
        }
    }}>
        <Box sx={{display: 'flex', alignItems: 'center', mb: 1.5}}>
            <Box sx={{ 
                p: 0.5, 
                borderRadius: '8px', 
                background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}25 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1
            }}>
                {React.cloneElement(icon, { sx: { ...icon.props.sx, fontSize: '18px' } })}
            </Box>
            <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                fontSize: '0.85rem', 
                color: colors.textPrimary,
                background: colors.primaryGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
            }}>
                {title}
            </Typography>
        </Box>
        {children}
    </Box>
);


const UploadRateCustomerLookup = () => {
    const token = localStorage.getItem('AuthorizationToken');
    const userId = localStorage.getItem('userId') || 'UnknownUser';
    
    // --- State Definitions ---
    const [activeView, setActiveView] = useState('customer'); // 'customer' or 'master'
    const [userFilterType, setUserFilterType] = useState('new'); // 'new', 'existing', or 'multiple'
    
    // Existing User Filter states
    const [existingUserAreas, setExistingUserAreas] = useState([]);
    const [selectedExistingArea, setSelectedExistingArea] = useState('');
    const [existingCustomers, setExistingCustomers] = useState([]);
    const [selectedExistingCustomer, setSelectedExistingCustomer] = useState(null);
    const [loadingExistingCustomers, setLoadingExistingCustomers] = useState(false);
    const [customerDetailsData, setCustomerDetailsData] = useState([]);
    const [customerSchemeId, setCustomerSchemeId] = useState('');
    const [loadingCustomerDetails, setLoadingCustomerDetails] = useState(false);
   const [existingCustomerFilter, setExistingCustomerFilter] = useState("");

    // Filter tab states for Rate Details vs Customers List
    const [detailsViewFilter, setDetailsViewFilter] = useState('rates'); // 'rates' or 'customers'
    const [customersListData, setCustomersListData] = useState([]);
    const [loadingCustomersList, setLoadingCustomersList] = useState(false);
    const [selectedCustomersToDelete, setSelectedCustomersToDelete] = useState([]);
 
    // Existing User Excel Upload states
    const [existingUserExcelFile, setExistingUserExcelFile] = useState(null);
    const [existingUserExcelFileName, setExistingUserExcelFileName] = useState('');
    const [existingUserExcelData, setExistingUserExcelData] = useState([]);
    const [existingUserValidationErrors, setExistingUserValidationErrors] = useState([]);
    const [isExistingUserExcelProcessing, setIsExistingUserExcelProcessing] = useState(false);
    const [isExistingUserUploading, setIsExistingUserUploading] = useState(false);
    const existingUserFileInputRef = useRef(null);
    const [loadingMapping, setLoadingMapping] = useState(false);
    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState('');
    
    // Multiple User states
    const [multipleUserAreas, setMultipleUserAreas] = useState([]);
    const [selectedMultipleArea, setSelectedMultipleArea] = useState('');
    const [rebateSchemes, setRebateSchemes] = useState([]);
    const [selectedRebateScheme, setSelectedRebateScheme] = useState('');
    const [customerTypes, setCustomerTypes] = useState([]);
    const [selectedCustomerType, setSelectedCustomerType] = useState('');
    const [multipleSearchType, setMultipleSearchType] = useState('CUSTCODE');
    const [multipleSearchInput, setMultipleSearchInput] = useState('');
    const [multipleSearchResults, setMultipleSearchResults] = useState([]);
    const [multipleSelectedCustomers, setMultipleSelectedCustomers] = useState([]);
    const [loadingMultipleSearch, setLoadingMultipleSearch] = useState(false);
    const [loadingMultipleAreas, setLoadingMultipleAreas] = useState(false);

    // MAP Scheme Modal states
    const [mapSchemeModalOpen, setMapSchemeModalOpen] = useState(false);
    const [selectedCustomerForMapping, setSelectedCustomerForMapping] = useState(null);
    const [availableRebateSchemes, setAvailableRebateSchemes] = useState([]);
    const [selectedMappingScheme, setSelectedMappingScheme] = useState('');
    const [loadingAvailableSchemes, setLoadingAvailableSchemes] = useState(false);
    const [schemeRateData, setSchemeRateData] = useState([]);
    const [loadingSchemeRateData, setLoadingSchemeRateData] = useState(false);
    const [loadingRebateSchemes, setLoadingRebateSchemes] = useState(false);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [savingMultipleCustomers, setSavingMultipleCustomers] = useState(false);
    const [searchType, setSearchType] = useState('CUSTCODE');
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [loadingCustomerTypes, setLoadingCustomerTypes] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [showCustomerSearchModal, setShowCustomerSearchModal] = useState(false);
    const [showMappingProgressDialog, setShowMappingProgressDialog] = useState(false);
    const [schemeChoice, setSchemeChoice] = useState('create');
    const [excelFile, setExcelFile] = useState(null);
    const [excelFileName, setExcelFileName] = useState('');
    const [excelDataPreview, setExcelDataPreview] = useState([]);
    const [excelValidationErrors, setExcelValidationErrors] = useState([]);
    const [isExcelProcessing, setIsExcelProcessing] = useState(false);
    const [validationProgress, setValidationProgress] = useState(0);
    const [validationStatus, setValidationStatus] = useState('');
    const [showValidationProgress, setShowValidationProgress] = useState(false);
    
    // Delete scheme states
    const [existingUserMode, setExistingUserMode] = useState('new-rate'); // 'new-rate' or 'delete-scheme'
    const [selectedCustomersForDelete, setSelectedCustomersForDelete] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState(0);
    const [deleteStatus, setDeleteStatus] = useState('');
    const [showDeleteProgress, setShowDeleteProgress] = useState(false);
    const [deleteResults, setDeleteResults] = useState([]);

    // Central API chain processing loader
    const [isApiChainProcessing, setIsApiChainProcessing] = useState(false);
    const [apiChainMessage, setApiChainMessage] = useState('');
    const fileInputRef = useRef(null);
    const [rateSchemes, setRateSchemes] = useState([]);
    const [selectedRateScheme, setSelectedRateScheme] = useState('');
    const [loadingRateSchemes, setLoadingRateSchemes] = useState(false);
    const [existingSchemeDetails, setExistingSchemeDetails] = useState([]);
    const [loadingSchemeDetails, setLoadingSchemeDetails] = useState(false);
    const [mappingProgress, setMappingProgress] = useState(0);
    const [mappingStatus, setMappingStatus] = useState('');
    const [totalCustomersToUpload, setTotalCustomersToUpload] = useState(0);
    const [currentCustomerUploadIndex, setCurrentCustomerUploadIndex] = useState(0);
    const [uploadResults, setUploadResults] = useState([]);

    // Master view specific states
    const [masterExcelFile, setMasterExcelFile] = useState(null);
    const [masterExcelFileName, setMasterExcelFileName] = useState('');
    const [masterExcelData, setMasterExcelData] = useState([]);
    const [masterValidationErrors, setMasterValidationErrors] = useState([]);
    const [masterRowErrors, setMasterRowErrors] = useState({});
    const [isMasterExcelProcessing, setIsMasterExcelProcessing] = useState(false);
    const [isMasterUploading, setIsMasterUploading] = useState(false);
    const masterFileInputRef = useRef(null);

    // Master validation states
    const [rebateSchemeCustomerData, setRebateSchemeCustomerData] = useState([]);
    const [isLoadingRebateSchemeData, setIsLoadingRebateSchemeData] = useState(false);
    const [masterUnmappedCustomers, setMasterUnmappedCustomers] = useState([]);
    const [showMasterMappingDialog, setShowMasterMappingDialog] = useState(false);

    // Search state variables
    const [customerListSearch, setCustomerListSearch] = useState('');
    const [rateTableSearch, setRateTableSearch] = useState('');

    // Customer mapping confirmation dialog states
    const [showMappingConfirmDialog, setShowMappingConfirmDialog] = useState(false);
    const [customersToMap, setCustomersToMap] = useState([]);
    const [selectedAreaName, setSelectedAreaName] = useState('');

    // --- Self-Contained Toast State ---
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const toastTimer = useRef();


    const handleExistingUserExcelUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    setExistingUserExcelFile(file);
    setExistingUserExcelFileName(file.name);
    setExistingUserExcelData([]);
    setExistingUserValidationErrors([]);
  }
};


    const showToast = (message, severity = 'info') => {
        clearTimeout(toastTimer.current);
        setToast({ open: true, message, severity });
        toastTimer.current = setTimeout(() => {
            setToast(prev => ({ ...prev, open: false }));
        }, 3000);
    };

    const handleCloseToast = () => {
        clearTimeout(toastTimer.current);
        setToast(prev => ({ ...prev, open: false }));
    };

    // Download sample Excel file with template
    const downloadSampleExcel = () => {
        try {
            // Create sample data with headers and example rows
            const sampleData = [
                {
                    ItemCode: 'ITEM001',
                    QualifyQty: 100,
                    Rate: 5.50,
                    ValidityUpto: '2025-12-31T23:59:59'
                },
                {
                    ItemCode: 'ITEM001',
                    QualifyQty: 200,
                    Rate: 7.00,
                    ValidityUpto: '2025-12-31T23:59:59'
                },
                {
                    ItemCode: 'ITEM002',
                    QualifyQty: 150,
                    Rate: 6.00,
                    ValidityUpto: '2025-12-31T23:59:59'
                },
                {
                    ItemCode: 'ITEM003',
                    QualifyQty: 50,
                    Rate: 4.50,
                    ValidityUpto: '2025-12-31T23:59:59'
                }
            ];

            // Create workbook and worksheet
            const worksheet = XLSX.utils.json_to_sheet(sampleData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'RateUpload');

            // Set column widths for better visibility
            worksheet['!cols'] = [
                { wch: 15 }, // ItemCode
                { wch: 15 }, // QualifyQty
                { wch: 12 }, // Rate
                { wch: 30 }  // ValidityUpto
            ];

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `HORECA_Rate_Template_${timestamp}.xlsx`;

            // Download the file
            XLSX.writeFile(workbook, filename);
            showToast('Sample Excel file downloaded successfully!', 'success');
        } catch (error) {
            console.error('Error downloading sample Excel:', error);
            showToast('Failed to download sample Excel file.', 'error');
        }
    };

    const tableData = schemeChoice === 'create' ? excelDataPreview : existingSchemeDetails;

    // --- Effects ---
    useEffect(() => {
        if (token) {
            fetchAreas();
            fetchCustomerTypes();
            // Set areas for existing users as well (using same area list)
            setExistingUserAreas(areas);
        } else {
            showToast('No authorization token found. Please login first.', 'error');
        }
    }, [token]);

    // Update existing user areas when main areas are loaded
    useEffect(() => {
        setExistingUserAreas(areas);
    }, [areas]);

    // Fetch customers list when switching to customers view
    useEffect(() => {
        if (detailsViewFilter === 'customers' && customerSchemeId && selectedExistingArea) {
            fetchRebateSchemeCustomers();
        }
    }, [detailsViewFilter, customerSchemeId, selectedExistingArea]);

    // Load data for Multiple User when selected
    useEffect(() => {
        if (userFilterType === 'multiple' && token) {
            fetchMultipleUserAreas();
            fetchRebateSchemes();
            fetchMultipleCustomerTypes();
        }
    }, [userFilterType, token]);

    useEffect(() => {
        if (schemeChoice === 'existing' && rateSchemes.length === 0) {
            fetchRateSchemes();
        }
        setExcelDataPreview([]);
        setExcelValidationErrors([]);
        if (schemeChoice === 'create') {
            setExistingSchemeDetails([]);
            setSelectedRateScheme('');
        }
    }, [schemeChoice]);

    // Load rebate scheme customer data when Master view is accessed
    useEffect(() => {
        if (activeView === 'master' && token && rebateSchemeCustomerData.length === 0) {
            fetchRebateSchemeCustomerData();
        }
    }, [activeView, token, rebateSchemeCustomerData.length]);

    // --- API & Data Handling ---
    const handleApiError = (message) => showToast(message, 'error');

    const fetchAreas = async () => {
        setLoadingAreas(true);
        try {
            const response = await fetch(`${local_url}/api/SFArea/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch areas');
            const data = await response.json();
            setAreas(data.map(area => ({ AreaId: area.sfAreaId, AreaName: area.sfAreaName })));
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingAreas(false);
        }
    };

    const fetchCustomerTypes = async () => {
        setLoadingCustomerTypes(true);
        try {
            const response = await fetch(`${local_url}/api/CustomerList/GetCustomerTypes/C`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch customer types');
            setCustomerTypes(await response.json());
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingCustomerTypes(false);
        }
    };

    // Fetch existing customers by area for existing user filter
    const fetchExistingCustomersByArea = async (areaId) => {
        if (!areaId) return;
        setLoadingExistingCustomers(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebateschemecustomerbyarea?AreaId=${areaId}&PricingSchemeType=HORECA`, { 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            if (!response.ok) throw new Error('Failed to fetch existing customers');
            const data = await response.json();
            console.log('Existing customers data:', data);
            setExistingCustomers(data);
            if (data.length > 0) {
                showToast(`Found ${data.length} existing customers in selected area.`, 'success');
            } else {
                showToast('No existing customers found in selected area.', 'info');
            }
        } catch (error) {
            handleApiError(error.message);
            setExistingCustomers([]);
        } finally {
            setLoadingExistingCustomers(false);
        }
    };

    // Insert areawisecustomer when customer doesn't exist in rebate scheme
    const insertAreawiseCustomer = async (areaId, customerId) => {
        try {
            const payload = {
                option: "I",
                Areaid: areaId,
                CustomerId: customerId,
                PricingSchemeType: "HORECA",
                CreditType: "CR",
                CreditLimitProposed: 0.00,
                CreatedUserId: userId
            };

            const response = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to insert areawisecustomer');
            const data = await response.json();
            console.log('Customer inserted successfully:', data);
            showToast('Customer added to area-wise scheme successfully!', 'success');
            return data;
        } catch (error) {
            console.error('Error inserting areawisecustomer:', error);
            showToast('Failed to add customer to area-wise scheme: ' + error.message, 'error');
            throw error;
        }
    };

    // Fetch customer rate details
    const fetchCustomerRateDetails = async (areaId, customerId) => {
        if (!areaId || !customerId) return;
        setLoadingCustomerDetails(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${areaId}&CustomerID=${customerId}`, { 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            if (!response.ok) throw new Error('Failed to fetch customer rate details');
            const data = await response.json();
            console.log('Customer rate details:', data);
            
            if (data.length > 0) {
                // Transform API data to match table structure
                const transformedData = data.map((item, index) => ({
                    sno: index + 1,
                    itemName: item.ItemName,
                    itemCode: item.ItemCode,
                    qualifyQty: item.QualifyQty,
                    rate: item.Rate
                }));
                
                setCustomerDetailsData(transformedData);
                // Get scheme ID from first item (all items should have same scheme ID)
                setCustomerSchemeId(data[0].RebateSchemeId || '');
                showToast(`Found ${data.length} rate details for customer.`, 'success');
            } else {
                setCustomerDetailsData([]);
                setCustomerSchemeId('');
                showToast('No rate details found for this customer.', 'info');
            }
        } catch (error) {
            handleApiError(error.message);
            setCustomerDetailsData([]);
            setCustomerSchemeId('');
        } finally {
            setLoadingCustomerDetails(false);
        }
    };

    // Fetch rebate scheme customers list
    const fetchRebateSchemeCustomers = async () => {
        if (!selectedExistingArea || !customerSchemeId) {
            console.log('Missing area or scheme ID for customer list fetch');
            return;
        }
        
        setLoadingCustomersList(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/getrebateschemecustomers?RebateschemeId=${customerSchemeId}&AreaId=${selectedExistingArea}&CustomerId=${selectedExistingCustomer?.CustomerId || ''}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Failed to fetch rebate scheme customers');
            const data = await response.json();
            console.log('Rebate scheme customers:', data);
            
            setCustomersListData(data);
            if (data.length === 0) {
                showToast('No customers found for this rebate scheme.', 'info');
            } else {
                showToast(`Found ${data.length} customers in this rebate scheme.`, 'success');
            }
        } catch (error) {
            console.error('Error fetching rebate scheme customers:', error);
            handleApiError(error.message);
            setCustomersListData([]);
        } finally {
            setLoadingCustomersList(false);
        }
    };

    // Handle customer selection for deletion
    const handleCustomerSelection = (customerId, isSelected) => {
        if (isSelected) {
            setSelectedCustomersToDelete([...selectedCustomersToDelete, customerId]);
        } else {
            setSelectedCustomersToDelete(selectedCustomersToDelete.filter(id => id !== customerId));
        }
    };

    // Handle select all customers
    const handleSelectAllCustomers = (isSelected) => {
        if (isSelected) {
            setSelectedCustomersToDelete(customersListData.map(customer => customer.CustomerId));
        } else {
            setSelectedCustomersToDelete([]);
        }
    };

    // Delete selected customers from rebate scheme

    const handleCustomerSearch = async () => {
        if (!selectedCustomerType || !searchInput.trim() || !selectedArea) {
            showToast('Please select Area, Customer Type, and enter a search term.', 'warning');
            return;
        }
        setLoadingSearch(true);
        try {
            const response = await fetch(`${local_url}/api/POSCustomerSerch/Get/${selectedCustomerType}/${searchType}/${searchInput}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error(`Search failed: ${response.statusText}`);
            const data = await response.json();
            let results = Array.isArray(data.customers) ? data.customers : [];

            results = results.map(customer => {
                if (customer.custID === "L102 1539") {
                    return { ...customer, schemeId: 'PRE-MAPPED-SCHEME-01' };
                }
                return customer;
            });

            setSearchResults(results);
            setShowCustomerSearchModal(true); // Always show modal on new search
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingSearch(false);
        }
    };

    const processExcelForNewScheme = async () => {
        if (!excelFile) return;
        setIsExcelProcessing(true);
        setShowValidationProgress(true);
        setValidationProgress(0);
        setValidationStatus('Reading Excel file...');

        const totalCustomers = selectedCustomers.length;
        const progressStep = totalCustomers > 0 ? 100 / totalCustomers : 100;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                setValidationProgress(10);
                setValidationStatus('Validating Excel structure...');
                await new Promise(resolve => setTimeout(resolve, 300));

                if (jsonData.length < 2) throw new Error('No data found in Excel file.');
                const headers = jsonData[0].map(h => String(h).trim());
                const requiredHeaders = ['ItemCode', 'Rate', 'QualifyQty'];
                if (requiredHeaders.some(h => !headers.includes(h))) throw new Error('Missing required columns: ItemCode, Rate, QualifyQty.');

                // Check if ValidityUpto column exists (optional but recommended)
                const hasValidityUpto = headers.includes('ValidityUpto');
                
                setValidationProgress(20);
                setValidationStatus('Processing Excel data...');
                await new Promise(resolve => setTimeout(resolve, 300));

                const processedData = [], newErrors = [];
                
                // Filter out empty rows before processing and keep track of original row numbers
                const dataRowsWithOriginalIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    // Check if row has any meaningful data (not all empty/null/undefined)
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithOriginalIndex.push({ row, originalRowNumber: originalIndex + 2 }); // +2 for header and 1-based indexing
                    }
                });
                
                const totalRows = dataRowsWithOriginalIndex.length;
                
                // Process rows with progressive updates
                for (let i = 0; i < totalRows; i++) {
                    const { row, originalRowNumber } = dataRowsWithOriginalIndex[i];
                    let hasRowError = false;
                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();
                    const qty = Number(row[headers.indexOf('QualifyQty')]);
                    const rate = Number(row[headers.indexOf('Rate')]);
                    const validityUptoCellValue = hasValidityUpto ? String(row[headers.indexOf('ValidityUpto')] || '').trim() : '';

                    // Validation logic
                    if (!itemCode) { newErrors.push(`Row ${originalRowNumber}: ItemCode missing.`); hasRowError = true; }
                    if (isNaN(qty) || qty < 0) { newErrors.push(`Row ${originalRowNumber}: Invalid Quantity - cannot be negative.`); hasRowError = true; }
                    if (isNaN(rate) || rate < 0) { newErrors.push(`Row ${originalRowNumber}: Invalid Rate - cannot be negative.`); hasRowError = true; }

                    // Validate ValidityUpto if provided - should be ISO date format
                    if (validityUptoCellValue && hasValidityUpto) {
                        const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
                        if (!dateRegex.test(validityUptoCellValue)) {
                            newErrors.push(`Row ${originalRowNumber}: Invalid ValidityUpto format. Use YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS format.`);
                            hasRowError = true;
                        }
                    }

                    // Business rule validation
                    if (!hasRowError) {
                        if (qty === 0 && rate > 0) { newErrors.push(`Row ${originalRowNumber}: QualifyQty cannot be 0 when Rate is greater than 0.`); hasRowError = true; }
                        if (rate === 0 && qty > 0) { newErrors.push(`Row ${originalRowNumber}: Rate cannot be 0 when QualifyQty is greater than 0.`); hasRowError = true; }
                    }

                    if (!hasRowError) {
                        const itemData = { ItemCode: itemCode, QualifyQty: qty, Rate: parseFloat(rate.toFixed(2)) };
                        // Add ValidityUpto if provided
                        if (validityUptoCellValue && hasValidityUpto) {
                            itemData.ValidityUpto = validityUptoCellValue;
                        }
                        processedData.push(itemData);
                    }

                    // Update progress based on row processing + customer count calculation
                    const rowProgress = 20 + (60 * (i + 1) / totalRows); // 20-80% for row processing
                    setValidationProgress(rowProgress);
                    setValidationStatus(`Processing row ${i + 1} of ${totalRows}...`);
                    
                    // Small delay every 10 rows for visual effect
                    if ((i + 1) % 10 === 0) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                // Final validation step based on customers
                if (totalCustomers > 0) {
                    setValidationStatus(`Calculating upload requirements for ${totalCustomers} customers...`);
                    for (let customerIndex = 0; customerIndex < totalCustomers; customerIndex++) {
                        const customer = selectedCustomers[customerIndex];
                        const customerName = customer.customerName || customer.CustomerName || 'Unknown';
                        setValidationStatus(`Validating for ${customerName}...`);
                        setValidationProgress(80 + (20 * (customerIndex + 1) / totalCustomers));
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                } else {
                    setValidationProgress(100);
                    setValidationStatus('Validation complete!');
                }

                // Set final data
                setExcelDataPreview(processedData);
                setExcelValidationErrors(newErrors);

                // Show completion message
                if (newErrors.length > 0) {
                    setValidationStatus(`Validation completed with ${newErrors.length} errors`);
                    showToast(`Validation failed with ${newErrors.length} errors.`, 'warning');
                } else {
                    setValidationStatus('Excel validated successfully!');
                    showToast(`Excel validated successfully! Ready to upload to ${totalCustomers || 1} customer(s).`, 'success');
                }

                // Close dialog after delay
                setTimeout(() => {
                    setShowValidationProgress(false);
                    setValidationProgress(0);
                }, 1500);

            } catch (err) {
                setValidationStatus('Validation failed');
                handleApiError('Error parsing Excel: ' + err.message);
                setTimeout(() => {
                    setShowValidationProgress(false);
                    setValidationProgress(0);
                }, 1500);
            } finally {
                setIsExcelProcessing(false);
            }
        };
        reader.readAsArrayBuffer(excelFile);
    };

    const performUpload = async (rebateSchemeType, isAreaWise = false) => {
        setLoadingMapping(true);
        setShowMappingProgressDialog(true);
        setUploadResults([]);
        const totalCustomers = selectedCustomers.length;
        setTotalCustomersToUpload(totalCustomers);
        setCurrentCustomerUploadIndex(0);
        
        const results = [];
        
        try {
            // Prepare the common excel data details
            const details = excelDataPreview.map(row => {
                const qualifyQty = row.QualifyQty || 
                                 row.QualifyQuantity ||
                                 row['Qualify Qty'] || 
                                 row['QualifyingQty'] || 
                                 row['Qualifying Qty'] || 
                                 row['Quantity'] || 
                                 row['Qty'] || 
                                 row['MinQty'] || 
                                 row['Min Qty'] || 
                                 row['Minimum Quantity'] || 
                                 0;

                const rate = row.Rate || 
                           row['Price'] || 
                           row['Unit Price'] || 
                           row['Rate Per Unit'] || 
                           row['Rebate Rate'] || 
                           row['Discount Rate'] || 
                           0;

                const itemCode = row.ItemCode || 
                               row['Item Code'] || 
                               row['ItemId'] || 
                               row['Item ID'] || 
                               row['Product Code'] || 
                               row['SKU'] || 
                               "";

                return {
                    itemCode: itemCode,
                    qualifyQty: parseFloat(qualifyQty) || 0,
                    rate: parseFloat(rate) || 0,
                    dml: "I"
                };
            });

            if (details.length === 0) {
                throw new Error('No valid items found in Excel file. Please check the data format.');
            }

            // Upload for each selected customer
            for (let i = 0; i < selectedCustomers.length; i++) {
                const customer = selectedCustomers[i];
                const customerName = customer.customerName || customer.CustomerName || 'Unknown Customer';
                const customerId = customer.custID || customer.CustCode || customer.CustomerId || customer.custCode;
                
                setCurrentCustomerUploadIndex(i);
                setMappingStatus(`Uploading for ${customerName}...`);
                setMappingProgress(0);
                
                try {
                    const requestBody = {
                        uniqueId: "",
                        rebateSchemeType: 'CUST',
                        areaId: isAreaWise ? selectedArea : null,
                        rebateSchemeId:'',
                        customerId: customerId,
                        dateOfEntry: (() => {
                            const now = new Date();
                            const istOffset = 5.5 * 60 * 60 * 1000;
                            const istDate = new Date(now.getTime() + istOffset);
                            return istDate.toISOString();
                        })(),
                        createdUserCode: userId,
                        rateDetail: details
                    };

                    console.log(`Uploading for customer ${i + 1}/${totalCustomers}:`, { customerName, customerId, requestBody });

                    setMappingProgress(30);
                    setMappingStatus(`Sending data for ${customerName}...`);

                    // Log POST API request details
                    const apiUrl = `${local_url}/api/CustomerRate`;
                    const apiPayload = JSON.stringify(requestBody);

                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log(`📤 POST API REQUEST - ${new Date().toISOString()}`);
                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log('URL:', apiUrl);
                    console.log('Method:', 'POST');
                    console.log('Headers:', {
                        'Content-Type': 'application/json',
                        'Authorization': '***Bearer Token***'
                    });
                    console.log('Request Body:', JSON.parse(apiPayload));
                    console.log('Total Items in Payload:', requestBody.rateDetail.length);
                    console.log('Items Details:', requestBody.rateDetail.map((item, idx) => ({
                        index: idx + 1,
                        itemCode: item.itemCode,
                        qualifyQty: item.qualifyQty,
                        rate: item.rate,
                        validityUpto: item.validityUpto || 'N/A',
                        dml: item.dml
                    })));
                    console.log('═══════════════════════════════════════════════════════════════');

                    const response = await fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: apiPayload
                    });

                    setMappingProgress(70);
                    setMappingStatus(`Processing response for ${customerName}...`);

                    if (!response.ok) {
                        const errorData = await response.text();
                        console.error('❌ API ERROR RESPONSE:', {
                            status: response.status,
                            statusText: response.statusText,
                            errorData: errorData
                        });
                        throw new Error(`API Error (${response.status}): ${errorData}`);
                    }

                    const result = await response.json();
                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log(`✅ POST API RESPONSE - ${new Date().toISOString()}`);
                    console.log('═══════════════════════════════════════════════════════════════');
                    console.log('Response Status:', response.status, response.statusText);
                    console.log('Response Data:', result);
                    console.log('Response type:', typeof result);
                    console.log('Response keys:', Object.keys(result || {}));
                    console.log('Description value:', result?.description);
                    console.log('═══════════════════════════════════════════════════════════════');

                    setMappingProgress(60);
                    setMappingStatus(`Processing encryption for ${customerName}...`);

                    // Call encryption and webhook APIs if description is available
                    if (result && result.description) {
                        console.log(`STARTING ENCRYPTION API CHAIN FOR ${customerName}...`);

                        try {
                            // Step 1: Call encryption API
                            const encryptionResponse = await fetch(`${local_url}/api/webhook-keys/encrypt`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    keyId: "12334311",
                                    webhookKey: result.description
                                })
                            });

                            if (encryptionResponse.ok) {
                                const encryptionResult = await encryptionResponse.json();
                                console.log(`Encryption API response for ${customerName}:`, encryptionResult);

                                setMappingProgress(80);
                                setMappingStatus(`Calling webhook for ${customerName}...`);

                                // Step 2: Call webhook API with encrypted key
                                if (encryptionResult && encryptionResult.encryptedKey) {
                                    try {
                                        // Remove == from encrypted key
                                        const cleanEncryptedKey = encryptionResult.encryptedKey.replace(/=+$/, '');

                                        const webhookPayload = {
                                            uId: "0000000001",
                                            id: cleanEncryptedKey,
                                            currStatus: "insert",
                                            updatedOn: new Date().toISOString(),
                                            message: result.description
                                        };

                                        console.log(`Webhook API payload for ${customerName}:`, webhookPayload);

                                        const webhookResponse = await fetch(`${web_hook}/WebHook/InsertHorecaRate`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify(webhookPayload)
                                        });

                                        if (webhookResponse.ok) {
                                            const webhookResult = await webhookResponse.json();
                                            console.log(`Webhook API success for ${customerName}:`, webhookResult);
                                            setMappingStatus(`All APIs completed for ${customerName}!`);
                                        } else {
                                            console.warn(`Webhook API failed for ${customerName}, but continuing with upload success`);
                                            setMappingStatus(`Upload successful for ${customerName}, webhook warning!`);
                                        }
                                    } catch (webhookError) {
                                        console.warn(`Webhook API error for ${customerName}:`, webhookError);
                                        setMappingStatus(`Upload successful for ${customerName}, webhook warning!`);
                                    }
                                } else {
                                    console.warn(`No encrypted key received for ${customerName}`);
                                    setMappingStatus(`Upload successful for ${customerName}, encryption warning!`);
                                }
                            } else {
                                console.warn(`Encryption API failed for ${customerName}, but continuing with upload success`);
                                setMappingStatus(`Upload successful for ${customerName}, encryption warning!`);
                            }
                        } catch (encryptionError) {
                            console.warn(`Encryption API error for ${customerName}:`, encryptionError);
                            setMappingStatus(`Upload successful for ${customerName}, encryption warning!`);
                        }
                    } else {
                        console.log(`ENCRYPTION API CHAIN NOT STARTED FOR ${customerName} - Missing result or description`);
                        console.log('Result exists:', !!result);
                        console.log('Description exists:', !!(result && result.description));
                    }

                    setMappingProgress(100);
                    setMappingStatus(`Completed processing for ${customerName}!`);

                    results.push({
                        customerName: customerName,
                        success: true,
                        result: result
                    });

                    setUploadResults([...results]);
                    
                    // Small delay between uploads
                    if (i < selectedCustomers.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }

                } catch (err) {
                    console.error(`Upload error for customer ${customerName}:`, err);
                    results.push({
                        customerName: customerName,
                        success: false,
                        error: err.message
                    });
                    setUploadResults([...results]);
                }
            }

            setCurrentCustomerUploadIndex(totalCustomers);
            
            // Show final summary
            const successCount = results.filter(r => r.success).length;
            const failureCount = results.filter(r => !r.success).length;
            
            if (successCount === totalCustomers) {
                setMappingStatus('All uploads completed successfully!');
                showToast(`Successfully uploaded scheme for all ${totalCustomers} customers!`, 'success');
            } else if (successCount > 0) {
                setMappingStatus(`${successCount} uploads successful, ${failureCount} failed`);
                showToast(`${successCount} successful, ${failureCount} failed uploads`, 'warning');
            } else {
                setMappingStatus('All uploads failed');
                showToast('All uploads failed. Please check the errors.', 'error');
            }

            // Reset state after delay
            setTimeout(() => {
                resetAllState();
                setShowMappingProgressDialog(false);
                setMappingProgress(0);
                setUploadResults([]);
                setTotalCustomersToUpload(0);
                setCurrentCustomerUploadIndex(0);
            }, 3000);

        } catch (err) {
            console.error('Upload process error:', err);
            handleApiError(`Upload process error: ${err.message}`);
            setMappingStatus('Upload process failed');
            setMappingProgress(0);
        } finally {
            setLoadingMapping(false);
        }
    };
    
    // --- Other handlers and utility functions ---
    const handleKeyPress = (event) => { if (event.key === 'Enter') handleCustomerSearch(); };
    const getSearchInputLabel = () => ({'CUSTCODE': 'Customer Code', 'MOBILE': 'Mobile Number', 'CUSTNAME': 'Customer Name'})[searchType] || 'Search Term';
    const handleFileChange = (event) => { const file = event.target.files[0]; if (file) { setExcelFile(file); setExcelFileName(file.name); setExcelDataPreview([]); setExcelValidationErrors([]); } };

    // Multiple User handlers
    const handleMultipleCustomerSearch = async () => {
        if (!selectedCustomerType || !multipleSearchInput.trim()) {
            handleApiError('Please select customer type and enter search term');
            return;
        }

        setLoadingMultipleSearch(true);
        try {
            const response = await fetch(
                `${local_url}/api/POSCustomerSerch/Get/${selectedCustomerType}/${multipleSearchType}/${encodeURIComponent(multipleSearchInput)}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            if (!response.ok) throw new Error('Search failed');
            const data = await response.json();
            
            // Handle the API response structure
            if (data.customers && Array.isArray(data.customers)) {
                setMultipleSearchResults(data.customers);
            } else if (Array.isArray(data)) {
                setMultipleSearchResults(data);
            } else {
                setMultipleSearchResults([data]);
            }
        } catch (error) {
            handleApiError('Search failed. Please try again.');
            console.error('Error searching customers:', error);
            setMultipleSearchResults([]);
        } finally {
            setLoadingMultipleSearch(false);
        }
    };

    const handleMultipleCustomerSelect = async (customer) => {
        const customerId = customer.custID || customer.customerCode;
        const isSelected = multipleSelectedCustomers.some(c => (c.custID || c.customerCode) === customerId);
        
        if (isSelected) {
            // Remove customer from selection
            setMultipleSelectedCustomers(prev => prev.filter(c => (c.custID || c.customerCode) !== customerId));
        } else {
            // Add customer to selection and check mapping status
            if (!selectedMultipleArea) {
                handleApiError('Please select an area first');
                return;
            }

            showToast(`Checking mapping status for ${customer.customerName}...`, 'info');
            
            try {
                // Call fetchrebateschemerates API to check if customer is already mapped
                const response = await fetch(`${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${selectedMultipleArea}&CustomerID=${customerId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    handleApiError('Failed to check customer mapping status');
                    return;
                }

                const data = await response.json();
                
                // Add mapping status to customer object
                const customerWithStatus = {
                    ...customer,
                    mappingStatus: data.length > 0 ? 'Mapped' : 'Not Mapped',
                    needsMapping: data.length === 0
                };
                
                if (data.length === 0) {
                    // Customer not mapped, show confirmation
                    const shouldMap = window.confirm(
                        `Customer "${customer.customerName}" is not mapped to the selected area and scheme. Do you want to map this customer to the area?`
                    );
                    
                    if (shouldMap) {
                        // Just add customer to selection - will be mapped when "Save Selected" is clicked
                        customerWithStatus.mappingStatus = 'Ready for mapping';
                        customerWithStatus.needsMapping = false;
                        
                        /*
                        // Removed individual API call - will be called on "Save Selected"
                        try {
                            showToast(`Mapping ${customer.customerName} to selected area...`, 'info');
                            
                            const mapResponse = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                    AreaId: selectedMultipleArea,
                                    CustomerID: customerId,
                                    CustomerName: customer.customerName,
                                    Mobile: customer.mobile,
                                    Address: customer.address || '',
                                    CreditType: "CR",
                                    CreditLimitProposed: 0.00,
                                    CreatedUserId: userId
                                })
                                
                            });

                            if (!mapResponse.ok) {
                                throw new Error('Failed to map customer');
                            }

                            customerWithStatus.mappingStatus = 'Mapped';
                            customerWithStatus.needsMapping = false;
                            showToast(`${customer.customerName} successfully mapped to area!`, 'success');
                        */
                    } else {
                        showToast('Customer selection cancelled', 'info');
                        return;
                    }
                }

                setMultipleSelectedCustomers(prev => [...prev, customerWithStatus]);
                
                if (data.length > 0) {
                    showToast(`${customer.customerName} is already mapped to the area`, 'success');
                } else {
                    showToast(`${customer.customerName} added to selection and mapped successfully`, 'success');
                }
                
            } catch (error) {
                console.error('Error checking customer mapping:', error);
                handleApiError('Failed to check customer mapping status');
            }
        }
    };

    const handleMapCustomer = async (customer) => {
        if (!selectedMultipleArea) {
            handleApiError('Please select an area first');
            return;
        }

        const customerId = customer.custID || customer.customerCode;
        showToast(`Mapping ${customer.customerName} to selected area...`, 'info');

        // Note: insertareawisecustomer API call removed - will be called on "Save Selected"
        /*
        try {
            const response = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    AreaId: selectedMultipleArea,
                    CustomerID: customerId,
                    CustomerName: customer.customerName,
                    Mobile: customer.mobile,
                    Address: customer.address || '',
                    CreditType: "CR",
                    CreditLimitProposed: 0.00,
                    CreatedUserId: userId
                })
            });

            if (!response.ok) {
                throw new Error('Failed to map customer');
            }

            const data = await response.json();
        */
            
        // Update customer status to show it's ready for batch processing
        setMultipleSelectedCustomers(prev => 
            prev.map(c => {
                const cId = c.custID || c.customerCode;
                if (cId === customerId) {
                    return {
                        ...c,
                        mappingStatus: 'Ready for mapping',
                        needsMapping: false
                    };
                }
                return c;
            })
        );
    };

    const handleSaveMultipleCustomers = async () => {
        if (multipleSelectedCustomers.length === 0) {
            handleApiError('Please select at least one customer');
            return;
        }

        if (!selectedRebateScheme) {
            handleApiError('Please select a rebate scheme');
            return;
        }

        // Show enhanced confirmation toast
        const customerNames = multipleSelectedCustomers.slice(0, 3).map(c => c.customerName).join(', ');
        const moreText = multipleSelectedCustomers.length > 3 ? ` and ${multipleSelectedCustomers.length - 3} more` : '';
        
        showToast(
            `🎯 Ready to map ${multipleSelectedCustomers.length} customers (${customerNames}${moreText}) to the selected rebate scheme. Click OK to proceed!`, 
            'warning'
        );
        
        const confirmed = window.confirm(
            `🎯 CONFIRM MAPPING\n\nYou are about to map ${multipleSelectedCustomers.length} selected customers:\n• ${customerNames}${moreText}\n\nTo the selected Rebate Scheme.\n\nThis action will update their pricing. Continue?`
        );

        if (!confirmed) return;

        setSavingMultipleCustomers(true);
        const results = [];
        const totalCustomers = multipleSelectedCustomers.length;

        try {
            showToast('Starting customer mapping process...', 'info');
            
            for (let i = 0; i < multipleSelectedCustomers.length; i++) {
                const customer = multipleSelectedCustomers[i];
                const customerId = customer.custID || customer.customerCode;
                
                try {
                    showToast(`Processing ${customer.customerName} (${i + 1}/${totalCustomers})...`, 'info');
                    
                    // First, call insertareawisecustomer API with new format
                    const areaCustomerBody = {
                        option: "I",
                        Areaid: selectedMultipleArea,
                        CustomerId: customerId,
                        PricingSchemeType: "HORECA",
                        CreditType: "CS",
                        CreditLimitProposed: 75000.00,
                        CreatedUserId: userId
                    };

                    const areaResponse = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(areaCustomerBody)
                    });

                    let skipToSecondApi = false;
                    
                    if (!areaResponse.ok) {
                        // Check if it's a 500 error indicating duplicate/already mapped customer
                        if (areaResponse.status === 500) {
                            try {
                                const errorData = await areaResponse.json();
                                if (errorData.details && errorData.details.includes('PRIMARY KEY constraint') && 
                                    errorData.details.includes('SFAreaCustomerRelationTable')) {
                                    // Customer is already mapped to area, skip to second API
                                    skipToSecondApi = true;
                                    showToast(`${customer.customerName} is already mapped to area, proceeding with rebate scheme mapping...`, 'info');
                                }
                            } catch (jsonError) {
                                // If we can't parse the error response, treat as regular error
                                throw new Error(`Failed to add customer to area: ${areaResponse.statusText}`);
                            }
                        }
                        
                        if (!skipToSecondApi) {
                            throw new Error(`Failed to add customer to area: ${areaResponse.statusText}`);
                        }
                    }
                    
                    // Then, map customer to rebate scheme using the correct API
                    const requestBody = {
                        AreaId: selectedMultipleArea,
                        CustomerId: customerId,
                        RebateSchemeId: selectedRebateScheme,
                        AddOnDiscountPc: 0,
                        DayCreditAllowed: true
                    };

                    const response = await fetch(`${local_signage}/api/v1/insertcustomerrebatescheme`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(requestBody)
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to map customer: ${response.statusText}`);
                    }

                    const result = await response.json();

                    // Check if insert was successful and call webhook
                    if (Array.isArray(result) && result.length > 0 && result[0].Details === "success") {
                        console.log(`Insert successful for ${customer.customerName}, calling getrebateschemelastuniqueid...`);

                        try {
                            // Get the CurrentRebateSchemeId from the response
                            const currentRebateSchemeId = result[0].CurrentRebateSchemeId;

                            // Call the get unique id API
                            const uniqueIdResponse = await fetch(`${local_signage}/api/v1/getrebateschemelastuniqueid?RebateSchemeId=${currentRebateSchemeId}`, {
                                method: 'GET',
                                headers: {
                                    'Authorization': `Bearer ${token}`
                                }
                            });

                            if (uniqueIdResponse.ok) {
                                const uniqueIdResult = await uniqueIdResponse.json();
                                console.log(`Got unique ID for inserted scheme ${currentRebateSchemeId}:`, uniqueIdResult);

                                // Get the UniqueId from the response
                                const uniqueId = uniqueIdResult[0]?.UniqueId;

                                if (uniqueId) {
                                    // Call webhook API with the UniqueId
                                    try {
                                        const webhookPayload = {
                                            "uId": "0000000001",
                                            "id": uniqueId,
                                            "currStatus": "update",
                                            "updatedOn": new Date().toISOString(),
                                            "message": "messafw"
                                        };

                                        const webhookResponse = await fetch(`${web_hook}/WebHook/InsertHorecaRate`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${token}`
                                            },
                                            body: JSON.stringify(webhookPayload)
                                        });

                                        if (webhookResponse.ok) {
                                            const webhookResult = await webhookResponse.json();
                                            console.log(`Webhook called successfully for inserted customer ${customer.customerName} with UniqueId ${uniqueId}:`, webhookResult);
                                        } else {
                                            console.warn(`Webhook call failed for inserted customer ${customer.customerName} with UniqueId ${uniqueId}:`, webhookResponse.status);
                                        }
                                    } catch (webhookError) {
                                        console.error(`Error calling webhook for inserted customer ${customer.customerName} with UniqueId ${uniqueId}:`, webhookError);
                                    }
                                }
                            } else {
                                console.warn(`Failed to get unique ID for inserted scheme ${currentRebateSchemeId}`);
                            }
                        } catch (uniqueIdError) {
                            console.error(`Error getting unique ID for inserted customer ${customer.customerName}:`, uniqueIdError);
                        }
                    }

                    results.push({ customer, status: 'success', result });
                    
                    // Small delay between requests
                    if (i < multipleSelectedCustomers.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 300));
                    }
                    
                } catch (error) {
                    console.error(`Error mapping customer ${customer.customerName}:`, error);
                    results.push({ customer, status: 'error', error: error.message });
                }
            }
            
            // Process results
            const successCount = results.filter(r => r.status === 'success').length;
            const errorCount = results.filter(r => r.status === 'error').length;
            
            if (successCount === totalCustomers) {
                showToast(`Successfully mapped all ${successCount} customers to the rebate scheme!`, 'success');
                setMultipleSelectedCustomers([]);
            } else if (successCount > 0) {
                showToast(`Mapped ${successCount} customers successfully, ${errorCount} failed`, 'warning');
            } else {
                showToast('Failed to map any customers. Please check and try again.', 'error');
            }
            
        } catch (error) {
            console.error('Error in save process:', error);
            showToast('Failed to complete customer mapping process. Please try again.', 'error');
        } finally {
            setSavingMultipleCustomers(false);
        }
    };
    
    // Multiple User fetch functions
    const fetchMultipleUserAreas = async () => {
        setLoadingMultipleAreas(true);
        try {
            const response = await fetch(`${local_url}/api/SFArea/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Failed to fetch areas');
            const data = await response.json();
            setMultipleUserAreas(data.map(area => ({ AreaId: area.sfAreaId, AreaName: area.sfAreaName })));
        } catch (error) {
            handleApiError(error.message);
        } finally {
            setLoadingMultipleAreas(false);
        }
    };

    const fetchRebateSchemes = async (areaId = selectedMultipleArea) => {
        if (!areaId) {
            setRebateSchemes([]);
            return;
        }
        setLoadingRebateSchemes(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebatescheme?RebateSchemeType=CUST&AreaId=${areaId}&CustomerID=ALL`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) throw new Error('Failed to fetch rebate schemes');
            const data = await response.json();
            setRebateSchemes(data);
        } catch (error) {
            handleApiError('Failed to load rebate schemes');
            console.error('Error fetching rebate schemes:', error);
        } finally {
            setLoadingRebateSchemes(false);
        }
    };

    const fetchMultipleCustomerTypes = async () => {
        if (loadingCustomerTypes) return; // Avoid duplicate calls
        setLoadingCustomerTypes(true);
        try {
            const response = await fetch(`${local_url}/api/CustomerList/GetCustomerTypes/C`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) throw new Error('Failed to fetch customer types');
            const data = await response.json();
            setCustomerTypes(data);
        } catch (error) {
            handleApiError('Failed to load customer types');
            console.error('Error fetching customer types:', error);
        } finally {
            setLoadingCustomerTypes(false);
        }
    };

    const fetchRateSchemes = async () => { 
        setLoadingRateSchemes(true); 
        try { 
            const dummySchemes = [
                { SchemeId: 'REGULAR-SCHEME-A', SchemeName: 'Regular Scheme A' },
                { SchemeId: 'PRE-MAPPED-SCHEME-01', SchemeName: 'Pre-Mapped Customer Scheme' },
                { SchemeId: 'FESTIVAL-OFFER', SchemeName: 'Festival Offer' },
            ];
            setRateSchemes(dummySchemes);
        } catch (error) { 
            handleApiError(error.message); 
        } finally { 
            setLoadingRateSchemes(false); 
        } 
    };

    const handleSelectExistingScheme = async (schemeId) => { 
        setSelectedRateScheme(schemeId); 
        if (!schemeId) { 
            setExistingSchemeDetails([]); 
            return; 
        } 
        setLoadingSchemeDetails(true); 
        try { 
            const dummyDetails = [
                { ItemCode: 'ITEM-001', QualifyQty: 10, Rate: 95.50 },
                { ItemCode: 'ITEM-002', QualifyQty: 5, Rate: 150.00 },
            ];
            await new Promise(resolve => setTimeout(resolve, 500));
            setExistingSchemeDetails(dummyDetails);
        } catch (error) { 
            handleApiError(error.message); 
        } finally { 
            setLoadingSchemeDetails(false); 
        } 
    };
    
    const handleCustomerSelect = (customer, event) => { 
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const id = customer.custID || customer.customerCode; 
        const isCurrentlySelected = selectedCustomers.some(c => (c.custID || c.customerCode) === id);
        
        // Multiple selection - add or remove from selection
        if (isCurrentlySelected) {
            setSelectedCustomers(prev => prev.filter(c => (c.custID || c.customerCode) !== id));
        } else {
            setSelectedCustomers(prev => [...prev, customer]);
        }
    };

    // Check customer rebate scheme status and collect customers that need mapping
    const checkCustomersForMapping = async (customers) => {
        const customersNeedingMapping = [];
        const results = [];
        
        for (const customer of customers) {
            const customerId = customer.custID || customer.CustCode || customer.CustomerId || customer.custCode;
            if (!customerId || !selectedArea) {
                results.push({ customer, status: 'skipped', reason: 'Missing customer ID or area' });
                continue;
            }

            try {
                showToast(`Checking rebate scheme for ${customer.customerName}...`, 'info');
                
                // Check if customer exists in rebate scheme
                const response = await fetch(`${local_signage}/api/v1/fetchrebateschemerates?RebateSchemeType=CUST&AreaId=${selectedArea}&CustomerID=${customerId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    results.push({ customer, status: 'error', reason: 'Failed to check rebate scheme' });
                    continue;
                }

                const data = await response.json();
                
                if (data.length === 0) {
                    // Customer doesn't exist, needs mapping
                    customersNeedingMapping.push(customer);
                } else {
                    // Customer already exists
                    results.push({ customer, status: 'exists', reason: 'Already in rebate scheme' });
                }
            } catch (error) {
                results.push({ customer, status: 'error', reason: error.message });
            }
        }
        
        return { customersNeedingMapping, results };
    };

    // Actually insert customers after confirmation
    const insertConfirmedCustomers = async (customers) => {
        const results = [];
        
        for (const customer of customers) {
            const customerId = customer.custID || customer.CustCode || customer.CustomerId || customer.custCode;
            
            try {
                showToast(`Adding ${customer.customerName} to rebate scheme...`, 'info');
                await insertAreawiseCustomer(selectedArea, customerId);
                results.push({ customer, status: 'inserted', reason: 'Successfully added to rebate scheme' });
            } catch (insertError) {
                results.push({ customer, status: 'insert_failed', reason: insertError.message });
            }
        }
        
        return results;
    };

    const handleConfirmCustomerSelection = async () => { 
        if (selectedCustomers.length === 0) { 
            showToast('Please select a customer.', 'warning'); 
            return; 
        } 
        
        // Check customers and collect those that need mapping
        showToast('Checking customer rebate scheme status...', 'info');
        const { customersNeedingMapping, results } = await checkCustomersForMapping(selectedCustomers);
        
        // Show immediate results for customers that already exist
        const existing = results.filter(r => r.status === 'exists').length;
        const failed = results.filter(r => r.status === 'error' || r.status === 'skipped').length;
        
        if (existing > 0) {
            showToast(`${existing} customer(s) already in rebate scheme.`, 'info');
        }
        if (failed > 0) {
            showToast(`${failed} customer(s) failed to check or had issues.`, 'warning');
        }
        
        // If there are customers that need mapping, show confirmation dialog
        if (customersNeedingMapping.length > 0) {
            // Get the area name for the selected area
            const selectedAreaData = areas.find(area => area.AreaId === selectedArea);
            const areaName = selectedAreaData ? selectedAreaData.AreaName : selectedArea;
            
            setCustomersToMap(customersNeedingMapping);
            setSelectedAreaName(areaName);
            setShowMappingConfirmDialog(true);
            return; // Don't proceed with the rest until user confirms
        }
        
        // If no customers need mapping, proceed directly
        proceedWithSchemeSelection();
    };

    // Handle confirmation dialog result
    const handleConfirmMapping = async () => {
        setShowMappingConfirmDialog(false);
        
        // Insert confirmed customers
        const insertResults = await insertConfirmedCustomers(customersToMap);
        
        // Show summary of all results
        const inserted = insertResults.filter(r => r.status === 'inserted').length;
        const insertFailed = insertResults.filter(r => r.status === 'insert_failed').length;
        
        let summaryMessage = '';
        if (inserted > 0) summaryMessage += `${inserted} customer(s) successfully added to rebate scheme. `;
        if (insertFailed > 0) summaryMessage += `${insertFailed} customer(s) failed to add. `;
        
        if (summaryMessage) {
            showToast(summaryMessage.trim(), insertFailed > 0 ? 'warning' : 'success');
        }
        
        // Clear the mapping data and proceed
        setCustomersToMap([]);
        setSelectedAreaName('');
        proceedWithSchemeSelection();
    };

    const handleCancelMapping = () => {
        setShowMappingConfirmDialog(false);
        setCustomersToMap([]);
        setSelectedAreaName('');
        // Don't proceed with scheme selection if user cancels
    };

    const proceedWithSchemeSelection = () => {
        // Check if any customer has a pre-mapped scheme
        const customersWithSchemes = selectedCustomers.filter(c => c.schemeId);
        const customersWithoutSchemes = selectedCustomers.filter(c => !c.schemeId);
        
        if (customersWithSchemes.length > 0) {
            // If some customers have schemes, use the first one found
            const firstMappedCustomer = customersWithSchemes[0];
            const schemeId = firstMappedCustomer.schemeId;
            
            if (customersWithoutSchemes.length > 0) {
                showToast(`${customersWithSchemes.length} customer(s) already mapped to schemes, ${customersWithoutSchemes.length} without schemes. Using existing scheme: ${schemeId}`, 'info');
            } else {
                showToast(`All selected customers are mapped to existing schemes. Using scheme: ${schemeId}`, 'info');
            }
            
            setSchemeChoice('existing');
            fetchRateSchemes().then(() => {
                setSelectedRateScheme(schemeId);
                handleSelectExistingScheme(schemeId);
            });
        } else {
            // No customers have pre-mapped schemes, default to create new
            showToast(`Customer selected. Ready to create new scheme.`, 'success');
            setSchemeChoice('create');
        }

        setShowCustomerSearchModal(false); 
    };

    const handleUploadNewScheme = async () => { 
        if (excelValidationErrors.length > 0 || excelDataPreview.length === 0) { 
            showToast('Please resolve errors or provide valid data.', 'error'); 
            return; 
        }
        
        // Validate that either area or customer is selected
        if (!selectedArea && selectedCustomers.length === 0) {
            showToast('Please select either an area or a customer before uploading.', 'error');
            return;
        }

        // Additional validation for Excel data structure
        const hasValidData = excelDataPreview.some(row => {
            const itemCode = row.ItemCode || row['Item Code'] || row['ItemId'] || row['Item ID'] || row['Product Code'] || row['SKU'];
            const qualifyQty = row.QualifyQty || row.QualifyQuantity || row['Qualify Qty'] || row['QualifyingQty'] || row['Qualifying Qty'] || row['Quantity'] || row['Qty'];
            const rate = row.Rate || row['Price'] || row['Unit Price'] || row['Rate Per Unit'] || row['Rebate Rate'] || row['Discount Rate'];
            
            return itemCode && (qualifyQty !== undefined && qualifyQty !== null && qualifyQty !== '') && (rate !== undefined && rate !== null && rate !== '');
        });

        if (!hasValidData) {
            showToast('Excel file must contain ItemCode, Rate, and QualifyQty columns with valid data.', 'error');
            return;
        }
        
        // Determine scheme type based on whether area is selected or customers are selected
        const rebateSchemeType = selectedArea ? "AREA" : "CUSTOMER";
        const isAreaWise = selectedArea ? true : false;
        
        await performUpload(rebateSchemeType, isAreaWise); 
    };
    
    const handleUploadExistingScheme = async () => { 
        if (!selectedRateScheme || existingSchemeDetails.length === 0) { 
            showToast('Please select a valid scheme.', 'error'); 
            return; 
        }
        
        // Validate that either area or customer is selected
        if (!selectedArea && selectedCustomers.length === 0) {
            showToast('Please select either an area or a customer before uploading.', 'error');
            return;
        }

        // Validate Excel data is available
        if (!excelDataPreview || excelDataPreview.length === 0) {
            showToast('Please upload and validate Excel data before proceeding.', 'error');
            return;
        }

        // Check for validation errors
        if (excelValidationErrors.length > 0) {
            showToast('Please resolve all validation errors in Excel data before uploading.', 'error');
            return;
        }

        // Validate required fields are present in Excel data
        const requiredFields = ['ItemCode', 'QualifyQty', 'Rate'];
        const missingFields = [];
        
        // Check if Excel data has required structure
        const sampleRow = excelDataPreview[0];
        if (sampleRow) {
            requiredFields.forEach(field => {
                const hasField = Object.keys(sampleRow).some(key => 
                    key.toLowerCase().includes(field.toLowerCase()) ||
                    (field === 'QualifyQty' && (key.toLowerCase().includes('qualify') || key.toLowerCase().includes('qty'))) ||
                    (field === 'ItemCode' && (key.toLowerCase().includes('item') || key.toLowerCase().includes('code'))) ||
                    (field === 'Rate' && key.toLowerCase().includes('rate'))
                );
                if (!hasField) {
                    missingFields.push(field);
                }
            });
        }

        if (missingFields.length > 0) {
            showToast(`Excel file is missing required columns: ${missingFields.join(', ')}. Please ensure your Excel file contains ItemCode, Rate, and QualifyQty columns.`, 'error');
            return;
        }
        
        // Determine scheme type based on whether area is selected or customers are selected
        const rebateSchemeType = selectedArea ? "AREA" : "CUSTOMER";
        const isAreaWise = selectedArea ? true : false;
        
        await performUpload(rebateSchemeType, isAreaWise); 
    };
    const resetAllState = () => { setSelectedCustomers([]); setSelectedArea(''); setExcelFile(null); setExcelFileName(''); setExcelDataPreview([]); setExcelValidationErrors([]); setExistingSchemeDetails([]); setSelectedRateScheme(''); setSchemeChoice('create'); };

    // Handle existing customer selection
    const handleExistingCustomerSelect = (customer) => {
        if (existingUserMode === 'new-rate') {
            // Existing functionality for new rate
            setSelectedExistingCustomer(customer);
            // Clear previous data
            setCustomerDetailsData([]);
            setCustomerSchemeId('');
            // Clear existing user Excel data
            setExistingUserExcelFile(null);
            setExistingUserExcelFileName('');
            setExistingUserExcelData([]);
            setExistingUserValidationErrors([]);
            // Fetch real customer rate details from API
            fetchCustomerRateDetails(selectedExistingArea, customer.CustomerId);
            showToast(`Selected customer: ${customer.CustomerName}`, 'info');
        } else if (existingUserMode === 'delete-scheme') {
            // Multiple selection for delete mode
            const customerId = customer.CustomerId;
            const isSelected = selectedCustomersForDelete.some(c => c.CustomerId === customerId);
            
            if (isSelected) {
                setSelectedCustomersForDelete(prev => prev.filter(c => c.CustomerId !== customerId));
                showToast(`Removed ${customer.CustomerName} from delete list`, 'info');
            } else {
                setSelectedCustomersForDelete(prev => [...prev, customer]);
                showToast(`Added ${customer.CustomerName} to delete list`, 'info');
            }
        }
    };

    // Handle delete selected customers
    const handleDeleteSelectedCustomers = async () => {
        if (selectedCustomersForDelete.length === 0) {
            showToast('No customers selected for deletion', 'warning');
            return;
        }

        const customerNames = selectedCustomersForDelete.map(c => c.CustomerName).join(', ');
        const confirmed = window.confirm(
            `Are you sure you want to delete rate schemes for the following customers?\n\n${customerNames}\n\nThis action cannot be undone.`
        );

        if (!confirmed) return;

        setIsDeleting(true);
        setShowDeleteProgress(true);
        setDeleteProgress(0);
        setDeleteResults([]);
        setDeleteStatus('Starting deletion process...');

        const totalCustomers = selectedCustomersForDelete.length;
        const results = [];

        try {
            for (let i = 0; i < selectedCustomersForDelete.length; i++) {
                const customer = selectedCustomersForDelete[i];
                const progress = ((i + 1) / totalCustomers) * 100;
                
                setDeleteProgress(progress);
                setDeleteStatus(`Deleting scheme for ${customer.CustomerName}... (${i + 1}/${totalCustomers})`);

                try {
                    const response = await fetch(`${local_signage}/api/v1/deletecustomerrebatescheme`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            AreaId: selectedExistingArea,
                            CustomerId: customer.CustomerId
                        })
                    });

                    if (!response.ok) {
                        const errorData = await response.text();
                        throw new Error(`API Error (${response.status}): ${errorData}`);
                    }

                    const result = await response.json();

                    // Check if delete was successful and automatically call getrebateschemelastuniqueid
                    if (Array.isArray(result) && result.length > 0 && result[0].Details === "OK") {
                        console.log(`Delete successful for ${customer.CustomerName}, calling getrebateschemelastuniqueid...`);

                        try {
                            // Extract RebateSchemeId from the delete response
                            const deletedRebateSchemeId = result[0].DeletedRebateSchemeId;

                            // Call the get unique id API in background - using the deleted customer's RebateSchemeId
                            const uniqueIdResponse = await fetch(`${local_signage}/api/v1/getrebateschemelastuniqueid?RebateSchemeId=${customer.RebateSchemeId}`, {
                                method: 'GET',
                                headers: {
                                    'Authorization': `Bearer ${token}`
                                }
                            });

                            if (uniqueIdResponse.ok) {
                                const uniqueIdResult = await uniqueIdResponse.json();
                                console.log(`Got unique ID for deleted scheme ${deletedRebateSchemeId}:`, uniqueIdResult);

                                // Store the unique ID in the result for reference
                                const uniqueId = uniqueIdResult[0]?.UniqueId;
                                result[0].UniqueId = uniqueId;

                                // Call webhook API with the UniqueId
                                if (uniqueId) {
                                    try {
                                        const webhookPayload = {
                                            "uId": "0000000001",
                                            "id": uniqueId,
                                            "currStatus": "update",
                                            "updatedOn": new Date().toISOString(),
                                            "message": "messafw"
                                        };

                                        const webhookResponse = await fetch(`${web_hook}/WebHook/InsertHorecaRate`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${token}`
                                            },
                                            body: JSON.stringify(webhookPayload)
                                        });

                                        if (webhookResponse.ok) {
                                            const webhookResult = await webhookResponse.json();
                                            console.log(`Webhook called successfully for UniqueId ${uniqueId}:`, webhookResult);
                                        } else {
                                            console.warn(`Webhook call failed for UniqueId ${uniqueId}:`, webhookResponse.status);
                                        }
                                    } catch (webhookError) {
                                        console.error(`Error calling webhook for UniqueId ${uniqueId}:`, webhookError);
                                    }
                                }
                            } else {
                                console.warn(`Failed to get unique ID for scheme ${deletedRebateSchemeId}`);
                            }
                        } catch (uniqueIdError) {
                            console.error(`Error getting unique ID:`, uniqueIdError);
                        }
                    }

                    results.push({
                        customerName: customer.CustomerName,
                        success: true,
                        result: result
                    });

                    console.log(`Successfully deleted scheme for ${customer.CustomerName}:`, result);

                } catch (err) {
                    console.error(`Delete error for ${customer.CustomerName}:`, err);
                    results.push({
                        customerName: customer.CustomerName,
                        success: false,
                        error: err.message
                    });
                }

                setDeleteResults([...results]);
                
                // Small delay between deletions
                if (i < selectedCustomersForDelete.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }

            // Show final summary
            const successCount = results.filter(r => r.success).length;
            const failureCount = results.filter(r => !r.success).length;

            if (successCount === totalCustomers) {
                setDeleteStatus('All schemes deleted successfully!');
                showToast(`Successfully deleted schemes for all ${totalCustomers} customers!`, 'success');
                
                // Refresh the customer list
                fetchExistingCustomersByArea(selectedExistingArea);
                
                // Clear selections
                setSelectedCustomersForDelete([]);
                setExistingUserMode('new-rate');
            } else if (successCount > 0) {
                setDeleteStatus(`${successCount} deleted successfully, ${failureCount} failed`);
                showToast(`${successCount} successful deletions, ${failureCount} failed`, 'warning');
            } else {
                setDeleteStatus('All deletions failed');
                showToast('All deletions failed. Please check the errors.', 'error');
            }

            // Close progress dialog after delay
            setTimeout(() => {
                setShowDeleteProgress(false);
                setDeleteProgress(0);
                setDeleteResults([]);
            }, 3000);

        } catch (err) {
            console.error('Delete process error:', err);
            showToast(`Delete process error: ${err.message}`, 'error');
            setDeleteStatus('Delete process failed');
        } finally {
            setIsDeleting(false);
        }
    };

    // Handle existing user Excel file change
    const handleExistingUserFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setExistingUserExcelFile(file);
            setExistingUserExcelFileName(file.name);
            setExistingUserExcelData([]);
            setExistingUserValidationErrors([]);
        }
    };

    // Clear functions for each upload type
    const clearNewUserExcel = () => {
        setExcelFile(null);
        setExcelFileName('');
        setExcelDataPreview([]);
        setExcelValidationErrors([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        showToast('Excel file cleared successfully', 'info');
    };

    const clearExistingUserExcel = () => {
        setExistingUserExcelFile(null);
        setExistingUserExcelFileName('');
        setExistingUserExcelData([]);
        setExistingUserValidationErrors([]);
        // Clear the table data as well
        setCustomerDetailsData([]);
        // Reset customer details to original state if they were from Excel processing
        if (selectedExistingCustomer && customerDetailsData.length > 0 && customerDetailsData[0]?.isFromExcel) {
            fetchCustomerRateDetails(selectedExistingArea, selectedExistingCustomer.CustomerId);
        }
        if (existingUserFileInputRef.current) {
            existingUserFileInputRef.current.value = '';
        }
        showToast('Excel file cleared successfully', 'info');
    };

    const clearMasterExcel = () => {
        setMasterExcelFile(null);
        setMasterExcelFileName('');
        setMasterExcelData([]);
        setMasterValidationErrors([]);
        setMasterRowErrors({});
        if (masterFileInputRef.current) {
            masterFileInputRef.current.value = '';
        }
        showToast('Excel file cleared successfully', 'info');
    };

    // MAP Scheme Modal Handlers
    const handleMapSchemeClick = (customer) => {
        setSelectedCustomerForMapping(customer);
        setMapSchemeModalOpen(true);
        fetchAvailableRebateSchemes(selectedExistingArea);
    };

    const handleCloseMapSchemeModal = () => {
        setMapSchemeModalOpen(false);
        setSelectedCustomerForMapping(null);
        setSelectedMappingScheme('');
        setSchemeRateData([]);
    };

    const fetchAvailableRebateSchemes = async (areaId) => {
        if (!areaId) return;

        setLoadingAvailableSchemes(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebatescheme?RebateSchemeType=CUST&AreaId=${areaId}&CustomerID=ALL`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setAvailableRebateSchemes(data);
            } else {
                showToast('Failed to fetch rebate schemes', 'error');
            }
        } catch (error) {
            console.error('Error fetching rebate schemes:', error);
            showToast('Error fetching rebate schemes', 'error');
        } finally {
            setLoadingAvailableSchemes(false);
        }
    };

    const fetchSchemeRateData = async (rebateSchemeId) => {
        if (!rebateSchemeId) return;

        setLoadingSchemeRateData(true);
        try {
            const today = new Date();
            const businessDate = today.getFullYear().toString() +
                String(today.getMonth() + 1).padStart(2, '0') +
                String(today.getDate()).padStart(2, '0');

            const response = await fetch(`${local_signage}/api/v1/rebateschemeratereport?BusinessDate=${businessDate}&AreaId=ALL&RebateSchemeId=${rebateSchemeId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.RebateRate && data.RebateRate.length > 0) {
                    // Flatten the itemline data for table display
                    const tableData = [];
                    data.RebateRate.forEach(area => {
                        if (area.itemline) {
                            area.itemline.forEach(item => {
                                tableData.push({
                                    AreaName: area.AreaName,
                                    ItemName: item.ItemName,
                                    Rate: item.Rate,
                                    QualifyQty: item.QualifyQty
                                });
                            });
                        }
                    });
                    setSchemeRateData(tableData);
                } else {
                    setSchemeRateData([]);
                }
            } else {
                showToast('Failed to fetch scheme rate data', 'error');
            }
        } catch (error) {
            console.error('Error fetching scheme rate data:', error);
            showToast('Error fetching scheme rate data', 'error');
        } finally {
            setLoadingSchemeRateData(false);
        }
    };

    const handleSchemeSelection = (schemeId) => {
        setSelectedMappingScheme(schemeId);
        if (schemeId) {
            fetchSchemeRateData(schemeId);
        } else {
            setSchemeRateData([]);
        }
    };

    const handleMapSchemeConfirm = async () => {
        if (!selectedCustomerForMapping || !selectedMappingScheme) {
            showToast('Please select a scheme to map', 'warning');
            return;
        }

        try {
            // Step 1: First call insertcustomerrebatescheme
            const requestBody = {
                AreaId: selectedExistingArea,
                CustomerId: selectedCustomerForMapping.CustomerId,
                RebateSchemeId: selectedMappingScheme,
                AddOnDiscountPc: 0,
                DayCreditAllowed: true
            };

            const response = await fetch(`${local_signage}/api/v1/insertcustomerrebatescheme`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Failed to map customer to scheme: ${response.statusText}`);
            }

            const result = await response.json();

            // Step 2: Check if mapping was successful and continue the process
            if (Array.isArray(result) && result.length > 0 && result[0].Details === "success") {
                console.log(`Mapping successful for ${selectedCustomerForMapping.CustomerName}, calling getrebateschemelastuniqueid...`);

                try {
                    // Step 3: Get the CurrentRebateSchemeId from the response
                    const currentRebateSchemeId = result[0].CurrentRebateSchemeId;

                    // Step 4: Second call - getrebateschemelastuniqueid
                    const uniqueIdResponse = await fetch(`${local_signage}/api/v1/getrebateschemelastuniqueid?RebateSchemeId=${currentRebateSchemeId}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (uniqueIdResponse.ok) {
                        const uniqueIdResult = await uniqueIdResponse.json();
                        console.log(`Got unique ID for mapped scheme ${currentRebateSchemeId}:`, uniqueIdResult);

                        // Step 5: Get the UniqueId from the response
                        const uniqueId = uniqueIdResult[0]?.UniqueId;

                        if (uniqueId) {
                            // Step 6: Third call - HorecaWebHook/InsertHorecaRate
                            try {
                                const webhookPayload = {
                                    "uId": "0000000001",
                                    "id": uniqueId,
                                    "currStatus": "update",
                                    "updatedOn": new Date().toISOString(),
                                    "message": "messafw"
                                };

                                const webhookResponse = await fetch(`${web_hook}/WebHook/InsertHorecaRate`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify(webhookPayload)
                                });

                                if (webhookResponse.ok) {
                                    const webhookResult = await webhookResponse.json();
                                    console.log(`Webhook called successfully for mapped customer ${selectedCustomerForMapping.CustomerName} with UniqueId ${uniqueId}:`, webhookResult);
                                } else {
                                    console.warn(`Webhook call failed for mapped customer ${selectedCustomerForMapping.CustomerName} with UniqueId ${uniqueId}:`, webhookResponse.status);
                                }
                            } catch (webhookError) {
                                console.error(`Error calling webhook for mapped customer ${selectedCustomerForMapping.CustomerName} with UniqueId ${uniqueId}:`, webhookError);
                            }
                        }
                    } else {
                        console.warn(`Failed to get unique ID for mapped scheme ${currentRebateSchemeId}`);
                    }
                } catch (uniqueIdError) {
                    console.error(`Error getting unique ID for mapped customer ${selectedCustomerForMapping.CustomerName}:`, uniqueIdError);
                }

                showToast(`Successfully mapped ${selectedCustomerForMapping.CustomerName} to rebate scheme!`, 'success');

                // Close modal and refresh customer list
                handleCloseMapSchemeModal();
                fetchExistingCustomersByArea(selectedExistingArea);

            } else {
                showToast('Failed to map customer to scheme', 'error');
            }

        } catch (error) {
            console.error('Error in mapping process:', error);
            showToast('Error in mapping process', 'error');
        }
    };

    // Process existing user Excel file for customers with no existing rates
    const processExistingUserExcelForNewCustomer = () => {
        if (!existingUserExcelFile) {
            showToast('Please upload an Excel file first.', 'error');
            return;
        }
        
        setIsExistingUserExcelProcessing(true);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                if (jsonData.length < 2) {
                    throw new Error('No data found in Excel file.');
                }
                
                const headers = jsonData[0].map(h => String(h).trim());
                const requiredHeaders = ['ItemCode', 'Rate', 'QualifyQty'];
                const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
                
                if (missingHeaders.length > 0) {
                    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
                }
                
                const processedData = [];
                const newErrors = [];
                
                // Filter out empty rows and track original row numbers
                const dataRowsWithIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithIndex.push({ row, originalRowNumber: originalIndex + 2 });
                    }
                });
                
                dataRowsWithIndex.forEach(({ row, originalRowNumber }) => {
                    let hasRowError = false;
                    
                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();
                    const qty = Number(row[headers.indexOf('QualifyQty')]);
                    const rate = Number(row[headers.indexOf('Rate')]);
                    
                    if (!itemCode) { 
                        newErrors.push(`Row ${originalRowNumber}: ItemCode missing.`); 
                        hasRowError = true; 
                    }
                    if (isNaN(qty) || qty < 0) { 
                        newErrors.push(`Row ${originalRowNumber}: Invalid Quantity - cannot be negative.`); 
                        hasRowError = true; 
                    }
                    if (isNaN(rate) || rate < 0) { 
                        newErrors.push(`Row ${originalRowNumber}: Invalid Rate - cannot be negative.`); 
                        hasRowError = true; 
                    }
                    
                    // Business rule validation: QualifyQty and Rate relationship
                    if (!hasRowError) {
                        if (qty === 0 && rate > 0) { 
                            newErrors.push(`Row ${originalRowNumber}: QualifyQty cannot be 0 when Rate is greater than 0.`); 
                            hasRowError = true; 
                        }
                        if (rate === 0 && qty > 0) { 
                            newErrors.push(`Row ${originalRowNumber}: Rate cannot be 0 when QualifyQty is greater than 0.`); 
                            hasRowError = true; 
                        }
                    }
                    
                    if (!hasRowError) {
                        processedData.push({
                            itemCode: itemCode,
                            qualifyQuantity: qty,
                            rate: rate
                        });
                    }
                });
                
                setExistingUserExcelData(processedData);
                setExistingUserValidationErrors(newErrors);
                
                // Create table data for display (since customer has no existing rates)
                const integratedCustomerDetails = processedData.map((item, index) => ({
                    sno: index + 1,
                    itemName: 'New Item', // Since this is a new customer with no existing data
                    itemCode: item.itemCode,
                    qualifyQty: '-', // No existing quantity
                    rate: '-', // No existing rate
                    hasExcelData: true,
                    newQty: item.qualifyQuantity,
                    newRate: item.rate,
                    isChanged: true, // Always true for new customers
                    isFromExcel: true
                }));
                
                // Update the customer details to show the new Excel data in table
                setCustomerDetailsData(integratedCustomerDetails);
                
                if (newErrors.length === 0) {
                    showToast(`Successfully processed ${processedData.length} items. Review the data below and click "Upload Rate Updates" to save!`, 'success');
                } else {
                    showToast(`Found ${newErrors.length} validation errors. Please fix them before uploading.`, 'warning');
                }
                
            } catch (error) {
                console.error('Excel processing error:', error);
                showToast(`Excel processing failed: ${error.message}`, 'error');
                setExistingUserExcelData([]);
                setExistingUserValidationErrors([error.message]);
                // Don't show table for failed processing
                setCustomerDetailsData([]);
            } finally {
                setIsExistingUserExcelProcessing(false);
            }
        };
        
        reader.readAsArrayBuffer(existingUserExcelFile);
    };

    // Process existing user Excel file and compare with current rates
    const processExistingUserExcel = () => {
        if (!existingUserExcelFile || !customerDetailsData.length) {
            showToast('Please ensure you have selected a customer and uploaded an Excel file.', 'error');
            return;
        }
        
        setIsExistingUserExcelProcessing(true);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                if (jsonData.length < 2) {
                    throw new Error('No data found in Excel file.');
                }
                
                const headers = jsonData[0].map(h => String(h).trim());
                const requiredHeaders = ['ItemCode', 'Rate', 'QualifyQty'];
                const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
                
                if (missingHeaders.length > 0) {
                    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
                }
                
                const processedData = [];
                const newErrors = [];
                const comparisonData = [];
                
                // Filter out empty rows and track original row numbers
                const dataRowsWithIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithIndex.push({ row, originalRowNumber: originalIndex + 2 });
                    }
                });
                
                dataRowsWithIndex.forEach(({ row, originalRowNumber }) => {
                    let hasRowError = false;
                    
                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();
                    const qty = Number(row[headers.indexOf('QualifyQty')]);
                    const rate = Number(row[headers.indexOf('Rate')]);
                    
                    if (!itemCode) { 
                        newErrors.push(`Row ${originalRowNumber}: ItemCode missing.`); 
                        hasRowError = true; 
                    }
                    if (isNaN(qty) || qty < 0) { 
                        newErrors.push(`Row ${originalRowNumber}: Invalid Quantity - cannot be negative.`); 
                        hasRowError = true; 
                    }
                    if (isNaN(rate) || rate < 0) { 
                        newErrors.push(`Row ${originalRowNumber}: Invalid Rate - cannot be negative.`); 
                        hasRowError = true; 
                    }
                    
                    // Business rule validation: QualifyQty and Rate relationship
                    if (!hasRowError) {
                        if (qty === 0 && rate > 0) { 
                            newErrors.push(`Row ${originalRowNumber}: QualifyQty cannot be 0 when Rate is greater than 0.`); 
                            hasRowError = true; 
                        }
                        if (rate === 0 && qty > 0) { 
                            newErrors.push(`Row ${originalRowNumber}: Rate cannot be 0 when QualifyQty is greater than 0.`); 
                            hasRowError = true; 
                        }
                    }
                    
                    if (!hasRowError) {
                        const processedRow = {
                            itemCode: itemCode,
                            qualifyQuantity: qty,
                            rate: parseFloat(rate.toFixed(2)),
                            hasError: false
                        };
                        processedData.push(processedRow);
                    }
                });
                
                // Create integrated customer details with comparison data
                const integratedCustomerDetails = [];
                const existingItemsMap = new Map();
                
                // First, add all existing customer items
                customerDetailsData.forEach((existing, index) => {
                    existingItemsMap.set(existing.itemCode, existing);
                    integratedCustomerDetails.push({
                        ...existing,
                        sno: index + 1,
                        hasExcelData: false,
                        newQty: null,
                        newRate: null,
                        isChanged: false,
                        isFromExcel: false
                    });
                });
                
                // Then, process Excel data and either update existing items or add new ones
                processedData.forEach(excelItem => {
                    const existingItem = existingItemsMap.get(excelItem.itemCode);
                    
                    if (existingItem) {
                        // Update existing item with Excel data
                        const existingIndex = integratedCustomerDetails.findIndex(item => item.itemCode === excelItem.itemCode);
                        if (existingIndex !== -1) {
                            integratedCustomerDetails[existingIndex] = {
                                ...integratedCustomerDetails[existingIndex],
                                hasExcelData: true,
                                newQty: excelItem.qualifyQuantity,
                                newRate: excelItem.rate,
                                isChanged: (existingItem.qualifyQty !== excelItem.qualifyQuantity) || (existingItem.rate !== excelItem.rate),
                                isFromExcel: false
                            };
                        }
                    } else {
                        // Add new item from Excel
                        integratedCustomerDetails.push({
                            sno: integratedCustomerDetails.length + 1,
                            itemName: 'New Item',
                            itemCode: excelItem.itemCode,
                            qualifyQty: '-',
                            rate: '-',
                            hasExcelData: true,
                            newQty: excelItem.qualifyQuantity,
                            newRate: excelItem.rate,
                            isChanged: true,
                            isFromExcel: true
                        });
                    }
                });
                
                setExistingUserExcelData(processedData);
                setExistingUserValidationErrors(newErrors);
                // Update the customer details to show comparison
                setCustomerDetailsData(integratedCustomerDetails);
                
                if (newErrors.length > 0) {
                    showToast(`Validation found ${newErrors.length} errors.`, 'warning');
                } else {
                    showToast(`Excel validated successfully! Found ${processedData.length} items for comparison.`, 'success');
                }
                
            } catch (err) {
                handleApiError('Error parsing Excel: ' + err.message);
            } finally {
                setIsExistingUserExcelProcessing(false);
            }
        };
        
        reader.readAsArrayBuffer(existingUserExcelFile);
    };

    // Handle existing user rate upload
    const handleExistingUserUpload = async () => {
        if (existingUserValidationErrors.length > 0 || existingUserExcelData.length === 0) {
            showToast('Please resolve validation errors before uploading.', 'error');
            return;
        }
        
        if (!selectedExistingCustomer || !selectedExistingArea) {
            showToast('Please ensure customer and area are selected.', 'error');
            return;
        }
        
        setIsExistingUserUploading(true);
        setLoadingMapping(true);
        setShowMappingProgressDialog(true);
        setMappingStatus('Uploading existing user rate updates...');
        setMappingProgress(0);
        
        try {
            // Prepare the request body using the same structure as new users
            const requestBody = {
                uniqueId: "",
                rebateSchemeType: 'CUST',
                areaId: selectedExistingArea,
                customerId: selectedExistingCustomer.CustomerId,
                dateOfEntry: (() => {
                    // Convert to IST (UTC+5:30)
                    const now = new Date();
                    const istOffset = 5.5 * 60 * 60 * 1000;
                    const istDate = new Date(now.getTime() + istOffset);
                    return istDate.toISOString();
                })(),
                createdUserCode: userId,
                rateDetail: existingUserExcelData.map(row => ({
                    itemCode: row.itemCode,
                    qualifyQty: parseFloat(row.qualifyQuantity) || 0,
                    rate: parseFloat(row.rate) || 0,
                    dml: "I"
                }))
            };
            
            console.log('Existing user upload request body:', requestBody);
            
            setMappingProgress(30);
            setMappingStatus('Sending updated rates to server...');
            
            const response = await fetch(`${local_url}/api/CustomerRate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });
            
            setMappingProgress(70);
            setMappingStatus('Processing response...');
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API Error (${response.status}): ${errorData}`);
            }
            
            const result = await response.json();
            console.log('CustomerRate API response:', result);
            console.log('Response type:', typeof result);
            console.log('Response keys:', Object.keys(result || {}));
            console.log('Description value:', result?.description);
            console.log('Result condition check:', !!(result && result.description));

            // Call encryption API with the description from CustomerRate response
            if (result && result.description) {
                console.log('STARTING ENCRYPTION API CHAIN...');
                setIsApiChainProcessing(true);
                setApiChainMessage('Starting encryption process...');
                setMappingProgress(80);
                setMappingStatus('Encrypting webhook key...');

                try {
                    const encryptionResponse = await fetch(`${local_url}/api/webhook-keys/encrypt`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            keyId: "12334311",
                            webhookKey: result.description
                        })
                    });

                    if (encryptionResponse.ok) {
                        const encryptionResult = await encryptionResponse.json();
                        console.log('Encryption API response:', encryptionResult);

                        // Call webhook API with encrypted key
                        if (encryptionResult && encryptionResult.encryptedKey) {
                            setApiChainMessage('Calling webhook API...');
                            setMappingProgress(90);
                            setMappingStatus('Calling webhook API...');

                            try {
                                // Remove == from encrypted key
                                const cleanEncryptedKey = encryptionResult.encryptedKey.replace(/=+$/, '');

                                const webhookPayload = {
                                    uId: "0000000001",
                                    id: cleanEncryptedKey,
                                    currStatus: "insert",
                                    updatedOn: new Date().toISOString(),
                                    message: result.description
                                };

                                console.log('Webhook API payload:', webhookPayload);

                                const webhookResponse = await fetch(`${web_hook}/WebHook/InsertHorecaRate`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify(webhookPayload)
                                });

                                if (webhookResponse.ok) {
                                    const webhookResult = await webhookResponse.json();
                                    console.log('Webhook API response:', webhookResult);
                                    setApiChainMessage('All APIs completed successfully!');
                                    setMappingStatus('Webhook call completed successfully!');
                                } else {
                                    console.warn('Webhook API failed, but continuing with upload success');
                                    setApiChainMessage('Upload successful, but webhook had issues');
                                    setMappingStatus('Upload successful, webhook warning!');
                                }
                            } catch (webhookError) {
                                console.warn('Webhook API error:', webhookError);
                                setMappingStatus('Upload successful, webhook warning!');
                            }
                        }

                        setMappingStatus('All operations completed successfully!');
                    } else {
                        console.warn('Encryption API failed, but continuing with upload success');
                        setMappingStatus('Upload successful, encryption warning!');
                    }
                } catch (encryptionError) {
                    console.warn('Encryption API error:', encryptionError);
                    setApiChainMessage('Upload successful, but encryption failed');
                    setMappingStatus('Upload successful, encryption warning!');
                } finally {
                    // Hide loader after 2 seconds to show final message
                    setTimeout(() => {
                        setIsApiChainProcessing(false);
                        setApiChainMessage('');
                    }, 2000);
                }
            } else {
                console.log('ENCRYPTION API CHAIN NOT STARTED - Missing result or description');
                console.log('Result exists:', !!result);
                console.log('Description exists:', !!(result && result.description));
            }

            setMappingProgress(100);
            setMappingStatus('Upload successful!');

            showToast('Rate updates uploaded successfully!', 'success');
            
            // Refresh customer details after successful upload
            setTimeout(() => {
                fetchCustomerRateDetails(selectedExistingArea, selectedExistingCustomer.CustomerId);
                // Reset upload state
                setExistingUserExcelFile(null);
                setExistingUserExcelFileName('');
                setExistingUserExcelData([]);
                setExistingUserValidationErrors([]);
                setShowMappingProgressDialog(false);
                setMappingProgress(0);
            }, 1500);
            
        } catch (err) {
            console.error('Existing user upload error:', err);
            handleApiError(`Upload error: ${err.message}`);
            setMappingStatus('Upload failed');
            setMappingProgress(0);
        } finally {
            setIsExistingUserUploading(false);
            setLoadingMapping(false);
        }
    };

    // Function to fetch rebate scheme customer data
    const fetchRebateSchemeCustomerData = async () => {
        setIsLoadingRebateSchemeData(true);
        try {
            const response = await fetch(`${local_signage}/api/v1/fetchrebateschemecustomerbyarea?AreaId=ALL&PricingSchemeType=HORECA`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch rebate scheme data: ${response.status}`);
            }

            const data = await response.json();
            console.log('Rebate scheme customer data loaded:', data);
            setRebateSchemeCustomerData(data || []);
        } catch (error) {
            console.error('Error fetching rebate scheme customer data:', error);
            showToast('Failed to load customer validation data. Upload validation may be incomplete.', 'warning');
            setRebateSchemeCustomerData([]);
        } finally {
            setIsLoadingRebateSchemeData(false);
        }
    };

    // Master view functions
    const handleMasterFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setMasterExcelFile(file);
            setMasterExcelFileName(file.name);
            setMasterExcelData([]);
            setMasterValidationErrors([]);
            setMasterRowErrors({});
        }
    };

    const processMasterExcel = () => {
        if (!masterExcelFile) return;
        setIsMasterExcelProcessing(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    throw new Error('No data found in Excel file.');
                }

                const headers = jsonData[0].map(h => String(h).trim());
                const requiredHeaders = ['AreaID', 'CustomerID', 'ItemCode', 'Rate', 'QualifyQty'];
                const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
                
                if (missingHeaders.length > 0) {
                    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
                }
                
                const processedData = [];
                const newErrors = [];
                const newRowErrors = {};
                
                // Filter out empty rows and track original row numbers
                const dataRowsWithIndex = [];
                jsonData.slice(1).forEach((row, originalIndex) => {
                    if (row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
                        dataRowsWithIndex.push({ row, originalRowNumber: originalIndex + 2 });
                    }
                });
                
                dataRowsWithIndex.forEach(({ row, originalRowNumber }) => {
                    const rowErrors = [];

                    const areaId = String(row[headers.indexOf('AreaID')] || '').trim();
                    const customerId = String(row[headers.indexOf('CustomerID')] || '').trim();
                    const itemCode = String(row[headers.indexOf('ItemCode')] || '').trim();
                    const qty = Number(row[headers.indexOf('QualifyQty')]);
                    const rate = Number(row[headers.indexOf('Rate')]);

                    if (!areaId) {
                        rowErrors.push('AreaID missing');
                        newErrors.push(`Row ${originalRowNumber}: AreaID missing.`);
                    }
                    if (!customerId) {
                        rowErrors.push('CustomerID missing');
                        newErrors.push(`Row ${originalRowNumber}: CustomerID missing.`);
                    }
                    if (!itemCode) {
                        rowErrors.push('ItemCode missing');
                        newErrors.push(`Row ${originalRowNumber}: ItemCode missing.`);
                    }
                    if (isNaN(qty) || qty < 0) {
                        rowErrors.push('Invalid Quantity');
                        newErrors.push(`Row ${originalRowNumber}: Invalid Quantity - cannot be negative.`);
                    }
                    if (isNaN(rate) || rate < 0) {
                        rowErrors.push('Invalid Rate');
                        newErrors.push(`Row ${originalRowNumber}: Invalid Rate - cannot be negative.`);
                    }

                    // Customer-Area mapping validation against API data
                    let mappingStatus = 'unknown';
                    let needsMapping = false;
                    if (areaId && customerId && rebateSchemeCustomerData.length > 0) {
                        // Find if customer exists in any area
                        const customerMappings = rebateSchemeCustomerData.filter(item =>
                            item.CustomerId === customerId
                        );

                        if (customerMappings.length === 0) {
                            // Customer not mapped to any area - needs mapping
                            mappingStatus = 'unmapped';
                            needsMapping = true;
                            rowErrors.push('Customer needs mapping');
                            newErrors.push(`Row ${originalRowNumber}: Customer ${customerId} is not mapped to any area. Needs mapping.`);
                        } else {
                            // Check if customer is mapped to the correct area
                            const correctAreaMapping = customerMappings.find(item =>
                                item.SFAreaId === areaId
                            );

                            if (correctAreaMapping) {
                                mappingStatus = 'correctly_mapped';
                            } else {
                                // Customer is mapped but to different area(s)
                                const mappedAreas = customerMappings.map(item => item.SFAreaId).join(', ');
                                mappingStatus = 'incorrectly_mapped';
                                rowErrors.push('Area mapping error');
                                newErrors.push(`Row ${originalRowNumber}: Customer ${customerId} is mapped to area(s): ${mappedAreas}, but Excel shows AreaID: ${areaId}.`);
                            }
                        }
                    }

                    // Business rule validation: QualifyQty and Rate relationship
                    if (rowErrors.length === 0) {
                        if (qty === 0 && rate > 0) {
                            rowErrors.push('QualifyQty-Rate Mismatch');
                            newErrors.push(`Row ${originalRowNumber}: QualifyQty cannot be 0 when Rate is greater than 0.`);
                        }
                        if (rate === 0 && qty > 0) {
                            rowErrors.push('Rate-QualifyQty Mismatch');
                            newErrors.push(`Row ${originalRowNumber}: Rate cannot be 0 when QualifyQty is greater than 0.`);
                        }
                    }
                    
                    if (rowErrors.length > 0) {
                        newRowErrors[originalRowNumber] = rowErrors;
                    }
                    
                    processedData.push({
                        rowNumber: originalRowNumber,
                        AreaID: areaId,
                        CustomerID: customerId,
                        ItemCode: itemCode,
                        QualifyQty: qty,
                        Rate: parseFloat(rate.toFixed(2)),
                        hasError: rowErrors.length > 0,
                        errors: rowErrors,
                        mappingStatus: mappingStatus,
                        needsMapping: needsMapping
                    });
                });
                
                setMasterExcelData(processedData);
                setMasterValidationErrors(newErrors);
                setMasterRowErrors(newRowErrors);

                // Collect unique unmapped customers for mapping functionality
                const unmappedCustomers = [];
                const customerMap = new Map();

                processedData.forEach(row => {
                    if (row.needsMapping && !customerMap.has(row.CustomerID)) {
                        customerMap.set(row.CustomerID, {
                            CustomerID: row.CustomerID,
                            AreaID: row.AreaID,
                            rowNumbers: [row.rowNumber]
                        });
                    } else if (row.needsMapping) {
                        customerMap.get(row.CustomerID).rowNumbers.push(row.rowNumber);
                    }
                });

                setMasterUnmappedCustomers(Array.from(customerMap.values()));

                if (newErrors.length > 0) {
                    const unmappedCount = Array.from(customerMap.values()).length;
                    if (unmappedCount > 0) {
                        showToast(`Validation found ${newErrors.length} errors in ${Object.keys(newRowErrors).length} rows. ${unmappedCount} customers need mapping.`, 'warning');
                    } else {
                        showToast(`Validation found ${newErrors.length} errors in ${Object.keys(newRowErrors).length} rows.`, 'warning');
                    }
                } else {
                    showToast('Excel validated successfully!', 'success');
                }
                
            } catch (err) {
                handleApiError('Error parsing Excel: ' + err.message);
            } finally {
                setIsMasterExcelProcessing(false);
            }
        };
        reader.readAsArrayBuffer(masterExcelFile);
    };

    const handleDownloadMasterSample = () => {
        const sampleData = [
            { AreaID: '00001', CustomerID: 'CUST001', ItemCode: 'ITM001', Rate: 150.00, QualifyQty: 10 },
            { AreaID: '00001', CustomerID: 'CUST001', ItemCode: 'ITM002', Rate: 200.50, QualifyQty: 5 },
            { AreaID: '00001', CustomerID: 'CUST002', ItemCode: 'ITM001', Rate: 175.00, QualifyQty: 8 },
            { AreaID: '00002', CustomerID: 'CUST003', ItemCode: 'ITM003', Rate: 120.75, QualifyQty: 15 },
            { AreaID: '00002', CustomerID: 'CUST003', ItemCode: 'ITM004', Rate: 300.00, QualifyQty: 3 }
        ];

        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Master Upload Sample');

        XLSX.writeFile(workbook, 'Master_Upload_Sample.xlsx');

        showToast('Sample Excel file downloaded successfully', 'success');
    };

    // Master mapping functions
    const handleMasterCustomerMapping = async (customerInfo) => {
        try {
            showToast(`Mapping customer ${customerInfo.CustomerID} to area ${customerInfo.AreaID}...`, 'info');

            const response = await fetch(`${local_signage}/api/v1/insertareawisecustomer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    option: "I",
                    Areaid: customerInfo.AreaID,
                    CustomerId: customerInfo.CustomerID,
                    PricingSchemeType: "HORECA",
                    CreditType: "CR",
                    CreditLimitProposed: 0.00,
                    CreatedUserId: userId
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to map customer: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Mapping result for ${customerInfo.CustomerID}:`, result);

            // Update rebate scheme customer data to include the new mapping
            setRebateSchemeCustomerData(prev => [
                ...prev,
                {
                    SFAreaId: customerInfo.AreaID,
                    CustomerId: customerInfo.CustomerID,
                    PricingSchemeType: "HORECA",
                    CreditType: "CR",
                    RebateSchemeId: null,
                    RebateSchemeName: null,
                    CustomerName: `Customer-${customerInfo.CustomerID}`,
                    Mobile: "0000000000",
                    CreatedDate: new Date().toISOString()
                }
            ]);

            // Remove from unmapped customers
            setMasterUnmappedCustomers(prev =>
                prev.filter(c => c.CustomerID !== customerInfo.CustomerID)
            );

            // Update Excel data to reflect correct mapping
            setMasterExcelData(prev => prev.map(row => {
                if (row.CustomerID === customerInfo.CustomerID && row.AreaID === customerInfo.AreaID) {
                    const updatedRow = {
                        ...row,
                        mappingStatus: 'correctly_mapped',
                        needsMapping: false,
                        hasError: row.errors.filter(err => err !== 'Customer needs mapping').length > 0,
                        errors: row.errors.filter(err => err !== 'Customer needs mapping')
                    };
                    return updatedRow;
                }
                return row;
            }));

            // Update validation errors
            setMasterValidationErrors(prev =>
                prev.filter(err => !err.includes(`Customer ${customerInfo.CustomerID}`) || !err.includes('not mapped to any area'))
            );

            showToast(`Successfully mapped customer ${customerInfo.CustomerID} to area ${customerInfo.AreaID}`, 'success');

        } catch (error) {
            console.error('Error mapping customer:', error);
            showToast(`Failed to map customer ${customerInfo.CustomerID}: ${error.message}`, 'error');
        }
    };

    const handleBulkMasterMapping = async () => {
        if (masterUnmappedCustomers.length === 0) return;

        showToast('Starting bulk customer mapping...', 'info');
        let successCount = 0;

        for (const customerInfo of masterUnmappedCustomers) {
            try {
                await handleMasterCustomerMapping(customerInfo);
                successCount++;
            } catch (error) {
                console.error(`Failed to map customer ${customerInfo.CustomerID}:`, error);
            }
        }

        if (successCount === masterUnmappedCustomers.length) {
            showToast(`Successfully mapped all ${successCount} customers`, 'success');
            setShowMasterMappingDialog(false);
        } else {
            showToast(`Mapped ${successCount} out of ${masterUnmappedCustomers.length} customers`, 'warning');
        }
    };

    const handleMasterUpload = async () => {
        if (masterValidationErrors.length > 0 || masterExcelData.length === 0) {
            showToast('Please resolve validation errors before uploading.', 'error');
            return;
        }

        setIsMasterUploading(true);
        setIsApiChainProcessing(true);
        setApiChainMessage('Processing master upload data...');
        try {
            // Group data by AreaID first, then by CustomerID
            const groupedData = {};

            masterExcelData.forEach(row => {
                const areaId = String(row.AreaID || '').trim();
                const customerId = String(row.CustomerID || '').trim();

                if (!areaId || !customerId) return; // Skip if essential fields are missing

                if (!groupedData[areaId]) {
                    groupedData[areaId] = {};
                }

                if (!groupedData[areaId][customerId]) {
                    groupedData[areaId][customerId] = [];
                }

                groupedData[areaId][customerId].push({
                    ItemCode: String(row.ItemCode || ''),
                    QualifyQty: parseFloat(row.QualifyQty) || 0,
                    Rate: parseFloat(row.Rate) || 0,
                    DML: "I"
                });
            });

            const apiErrors = [];
            let successCount = 0;

            // Make API calls for each area and customer combination
            for (const areaId of Object.keys(groupedData)) {
                for (const customerId of Object.keys(groupedData[areaId])) {
                    const rateDetails = groupedData[areaId][customerId];

                    try {
                        const requestBody = {
                            uniqueId: "",
                            rebateSchemeType: 'CUST',
                            areaId: areaId,
                            rebateSchemeId: '',
                            customerId: customerId,
                            dateOfEntry: (() => {
                                const now = new Date();
                                const istOffset = 5.5 * 60 * 60 * 1000;
                                const istDate = new Date(now.getTime() + istOffset);
                                return istDate.toISOString();
                            })(),
                            createdUserCode: userId,
                            rateDetail: rateDetails
                        };

                        // Log Master Upload API request details
                        const apiUrl = `${local_url}/api/CustomerRate`;
                        const apiPayload = JSON.stringify(requestBody);

                        console.log('═══════════════════════════════════════════════════════════════');
                        console.log(`📤 MASTER UPLOAD POST API REQUEST - ${new Date().toISOString()}`);
                        console.log('═══════════════════════════════════════════════════════════════');
                        console.log('Area ID:', areaId);
                        console.log('Customer ID:', customerId);
                        console.log('URL:', apiUrl);
                        console.log('Method:', 'POST');
                        console.log('Headers:', {
                            'Content-Type': 'application/json',
                            'Authorization': '***Bearer Token***'
                        });
                        console.log('Request Body:', JSON.parse(apiPayload));
                        console.log('Total Items in Payload:', rateDetails.length);
                        console.log('Items Details:', rateDetails.map((item, idx) => ({
                            index: idx + 1,
                            itemCode: item.ItemCode,
                            qualifyQty: item.QualifyQty,
                            rate: item.Rate,
                            dml: item.DML
                        })));
                        console.log('═══════════════════════════════════════════════════════════════');

                        const response = await fetch(apiUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: apiPayload
                        });

                        if (!response.ok) {
                            const errorData = await response.text();
                            console.error('❌ MASTER UPLOAD API ERROR RESPONSE:', {
                                areaId: areaId,
                                customerId: customerId,
                                status: response.status,
                                statusText: response.statusText,
                                errorData: errorData
                            });
                            apiErrors.push(`Area ${areaId}, Customer ${customerId}: ${errorData}`);
                        } else {
                            const result = await response.json();
                            console.log('═══════════════════════════════════════════════════════════════');
                            console.log(`✅ MASTER UPLOAD POST API RESPONSE - ${new Date().toISOString()}`);
                            console.log('═══════════════════════════════════════════════════════════════');
                            console.log('Area ID:', areaId);
                            console.log('Customer ID:', customerId);
                            console.log('Response Status:', response.status, response.statusText);
                            console.log('Response Data:', result);
                            console.log('═══════════════════════════════════════════════════════════════');
                            console.log(`CustomerRate API response for Area ${areaId}, Customer ${customerId}:`, result);

                            // Call encryption API with the description from CustomerRate response
                            if (result && result.description) {
                                console.log(`STARTING ENCRYPTION API CHAIN for Area ${areaId}, Customer ${customerId}...`);
                                setApiChainMessage(`Processing encryption for Area ${areaId}, Customer ${customerId}...`);

                                try {
                                    const encryptionResponse = await fetch(`${local_url}/api/webhook-keys/encrypt`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                            keyId: "12334311",
                                            webhookKey: result.description
                                        })
                                    });

                                    if (encryptionResponse.ok) {
                                        const encryptionResult = await encryptionResponse.json();
                                        console.log(`Encryption API response for Area ${areaId}, Customer ${customerId}:`, encryptionResult);

                                        // Call webhook API with encrypted key
                                        if (encryptionResult && encryptionResult.encryptedKey) {
                                            setApiChainMessage(`Calling webhook for Area ${areaId}, Customer ${customerId}...`);
                                            try {
                                                // Remove == from encrypted key
                                                const cleanEncryptedKey = encryptionResult.encryptedKey.replace(/=+$/, '');

                                                const webhookPayload = {
                                                    uId: "0000000001",
                                                    id: cleanEncryptedKey,
                                                    currStatus: "insert",
                                                    updatedOn: new Date().toISOString(),
                                                    message: result.description
                                                };

                                                console.log(`Webhook API payload for Area ${areaId}, Customer ${customerId}:`, webhookPayload);

                                                const webhookResponse = await fetch(`${web_hook}/WebHook/InsertHorecaRate`, {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json'
                                                    },
                                                    body: JSON.stringify(webhookPayload)
                                                });

                                                if (webhookResponse.ok) {
                                                    const webhookResult = await webhookResponse.json();
                                                    console.log(`Webhook API success for Area ${areaId}, Customer ${customerId}:`, webhookResult);
                                                } else {
                                                    console.warn(`Webhook API failed for Area ${areaId}, Customer ${customerId}, but continuing`);
                                                }
                                            } catch (webhookError) {
                                                console.warn(`Webhook API error for Area ${areaId}, Customer ${customerId}:`, webhookError);
                                            }
                                        }
                                    } else {
                                        console.warn(`Encryption API failed for Area ${areaId}, Customer ${customerId}, but continuing`);
                                    }
                                } catch (encryptionError) {
                                    console.warn(`Encryption API error for Area ${areaId}, Customer ${customerId}:`, encryptionError);
                                }
                            } else {
                                console.log(`No description found in response for Area ${areaId}, Customer ${customerId} - skipping encryption chain`);
                            }

                            successCount++;
                        }

                    } catch (error) {
                        apiErrors.push(`Area ${areaId}, Customer ${customerId}: ${error.message}`);
                    }
                }
            }

            if (apiErrors.length > 0) {
                setApiChainMessage(`Upload completed with ${successCount} successes and ${apiErrors.length} errors`);
                showToast(`Upload completed with ${successCount} successes and ${apiErrors.length} errors. Check details.`, 'warning');
                console.log('API Errors:', apiErrors);
            } else {
                setApiChainMessage(`Master upload successful! ${successCount} combinations processed.`);
                showToast(`Master rate data uploaded successfully! ${successCount} customer-area combinations processed.`, 'success');
            }

            // Reset master state after upload attempt
            setMasterExcelFile(null);
            setMasterExcelFileName('');
            setMasterExcelData([]);
            setMasterValidationErrors([]);
            setMasterRowErrors({});

        } catch (err) {
            setApiChainMessage('Upload error occurred');
            handleApiError(`Upload error: ${err.message}`);
        } finally {
            setIsMasterUploading(false);
            // Hide loader after 2 seconds to show final message
            setTimeout(() => {
                setIsApiChainProcessing(false);
                setApiChainMessage('');
            }, 2000);
        }
    };

    // Filtered customers for the customer list
    const filteredExistingCustomers = existingCustomers.filter(c =>
      (c.CustomerId || '').toLowerCase().includes(customerListSearch.toLowerCase()) ||
      (c.CustomerName || '').toLowerCase().includes(customerListSearch.toLowerCase()) ||
      (c.Mobile || '').toLowerCase().includes(customerListSearch.toLowerCase())
    ).sort((a, b) => {
      // Sort by RebateSchemeName, with customers without schemes at the end
      const aScheme = a.RebateSchemeName || '';
      const bScheme = b.RebateSchemeName || '';
      
      // If both have schemes, sort alphabetically by scheme name
      if (aScheme && bScheme) {
        return aScheme.localeCompare(bScheme);
      }
      
      // If only one has a scheme, put the one with scheme first
      if (aScheme && !bScheme) return -1;
      if (!aScheme && bScheme) return 1;
      
      // If neither has a scheme, sort by customer name
      return (a.CustomerName || '').localeCompare(b.CustomerName || '');
    });

    // Calculate customers with and without schemes
    const customersWithSchemes = filteredExistingCustomers.filter(c => c.RebateSchemeName && c.RebateSchemeName.trim() !== '').length;
    const customersWithoutSchemes = filteredExistingCustomers.length - customersWithSchemes;

    // Filtered rate details for the table
    const filteredCustomerDetailsData = customerDetailsData.filter(row =>
      (row.itemName || '').toLowerCase().includes(rateTableSearch.toLowerCase()) ||
      (row.itemCode || '').toLowerCase().includes(rateTableSearch.toLowerCase())
    );

    // --- Main Render ---
    return (
        <>
            <Header />
            <Box sx={{ 
                width: '100vw',
                height: '90vh',
                background: colors.backgroundAccent,
                overflow: 'auto',
                p:0.5,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Container maxWidth="xl" sx={{ p: '0 !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Paper elevation={0} sx={{ 
                        ...glassmorphismStyle,
                        p: 1, 
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        background: colors.backgroundCard,
                        border: `1px solid ${colors.border}`,
                        boxShadow: colors.shadowXl
                    }}>
                        {/* Fixed Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonSearchIcon sx={{ mr: 1.5, color: colors.primary, fontSize: '28px' }} />
                                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary }}>Customer Rate Upload</Typography>
                            </Box>
                            
                            {/* User Filter Toggle Buttons */}
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 0.5,
                                p: 0.5,
                                background: colors.backgroundGray,
                                borderRadius: '12px',
                                border: `1px solid ${colors.border}`,
                                boxShadow: 'inset 0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <Button
                                    onClick={() => {
                                        setUserFilterType('new');
                                        setActiveView('customer');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: userFilterType === 'new' ? colors.primaryGradient : 'transparent',
                                        color: userFilterType === 'new' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: userFilterType === 'new' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: userFilterType === 'new' 
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: userFilterType === 'new' ? colors.white : colors.white,
                                            transform: userFilterType === 'new' ? 'none' : 'translateY(-1px)',
                                            boxShadow: userFilterType === 'new' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    New User
                                </Button>
                                <Button
                                    onClick={() => {
                                        setUserFilterType('existing');
                                        setActiveView('customer');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: userFilterType === 'existing' ? colors.primaryGradient : 'transparent',
                                        color: userFilterType === 'existing' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: userFilterType === 'existing' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: userFilterType === 'existing' 
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: userFilterType === 'existing' ? colors.white : colors.white,
                                            transform: userFilterType === 'existing' ? 'none' : 'translateY(-1px)',
                                            boxShadow: userFilterType === 'existing' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    Existing User
                                </Button>
                                <Button
                                    onClick={() => {
                                        setUserFilterType('multiple');
                                        setActiveView('customer');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: userFilterType === 'multiple' ? colors.primaryGradient : 'transparent',
                                        color: userFilterType === 'multiple' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: userFilterType === 'multiple' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: userFilterType === 'multiple' 
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: userFilterType === 'multiple' ? colors.white : colors.white,
                                            transform: userFilterType === 'multiple' ? 'none' : 'translateY(-1px)',
                                            boxShadow: userFilterType === 'multiple' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    Multiple User
                                </Button>
                                <Button
                                    onClick={() => {
                                        setActiveView('master');
                                        setUserFilterType('master'); // Set this to distinguish from customer views
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        minWidth: 'auto',
                                        background: activeView === 'master' ? colors.primaryGradient : 'transparent',
                                        color: activeView === 'master' ? colors.white : colors.textSecondary,
                                        border: 'none',
                                        boxShadow: activeView === 'master' ? colors.shadowMd : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: activeView === 'master'
                                                ? `linear-gradient(135deg, #5b69eb 0%, #8b5ba6 100%)`
                                                : `${colors.primary}10`,
                                            color: activeView === 'master' ? colors.white : colors.white,
                                            transform: activeView === 'master' ? 'none' : 'translateY(-1px)',
                                            boxShadow: activeView === 'master' ? colors.shadowMd : colors.shadow,
                                        }
                                    }}
                                >
                                    Master Upload
                                </Button>
                            </Box>
                        </Box>
                        
                        {/* Conditional Content Based on User Filter Type */}
                        {userFilterType === 'new' && (
                            <>
                                {/* New User - Compact Search Controls */}
                                <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Area</InputLabel>
                                                <Select 
                                                    value={selectedArea} 
                                                    label="Area" 
                                                    onChange={(e) => setSelectedArea(e.target.value)} 
                                                    disabled={loadingAreas}
                                                    sx={{ 
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': { 
                                                            padding: '6px 8px', 
                                                            fontSize: '0.8rem' 
                                                        }
                                                    }}
                                                >
                                                    {areas.map((area) => ( 
                                                        <MenuItem key={area.AreaId} value={area.AreaId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {area.AreaName}
                                                        </MenuItem> 
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Customer Type</InputLabel>
                                                <Select 
                                                    value={selectedCustomerType} 
                                                    label="Customer Type" 
                                                    onChange={(e) => setSelectedCustomerType(e.target.value)} 
                                                    disabled={loadingCustomerTypes}
                                                    sx={{ 
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': { 
                                                            padding: '6px 8px', 
                                                            fontSize: '0.8rem' 
                                                        }
                                                    }}
                                                >
                                                    {customerTypes.map((type) => ( 
                                                        <MenuItem key={type.customerTypeCode} value={type.customerTypeCode} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {type.customerTypeName}
                                                        </MenuItem> 
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <RadioGroup 
                                                row 
                                                value={searchType} 
                                                onChange={(e) => setSearchType(e.target.value)}
                                                sx={{ justifyContent: 'center', gap: 0.5 }}
                                            >
                                                <FormControlLabel 
                                                    value="CUSTCODE" 
                                                    control={<Radio size="small" sx={{ py: 0.5 }} />} 
                                                    label="Code"
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                                />
                                                <FormControlLabel 
                                                    value="MOBILE" 
                                                    control={<Radio size="small" sx={{ py: 0.5 }} />} 
                                                    label="Mobile"
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                                />
                                                <FormControlLabel 
                                                    value="CUSTNAME" 
                                                    control={<Radio size="small" sx={{ py: 0.5 }} />} 
                                                    label="Name"
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                                />
                                            </RadioGroup>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <TextField 
                                                    fullWidth 
                                                    size="small" 
                                                    label={getSearchInputLabel()} 
                                                    value={searchInput} 
                                                    onChange={(e) => setSearchInput(e.target.value)} 
                                                    onKeyPress={handleKeyPress} 
                                                    sx={{ 
                                                        '& .MuiOutlinedInput-root': { 
                                                            borderRadius: '8px',
                                                            height: '32px'
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            fontSize: '0.8rem'
                                                        }
                                                    }} 
                                                />
                                                <Button 
                                                    variant="contained" 
                                                    onClick={handleCustomerSearch} 
                                                    disabled={loadingSearch} 
                                                    size="small"
                                                    sx={{ 
                                                        minWidth: '32px',
                                                        width: '32px',
                                                        height: '32px',
                                                        p: 0,
                                                        backgroundColor: colors.primary,
                                                        '&:hover': { backgroundColor: colors.primaryDark }
                                                    }}
                                                >
                                                    {loadingSearch ? <CircularProgress size={16} color="inherit" /> : <SearchIcon sx={{ fontSize: '16px' }} />}
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </>
                        )}

                        {userFilterType === 'existing' && (
                            <>
                                {/* Existing User - Compact Area Selection and Mode Buttons */}
                                <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                    <Grid container spacing={1.5} alignItems="center">
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Select Area</InputLabel>
                                                <Select 
                                                    value={selectedExistingArea} 
                                                    label="Select Area" 
                                                    onChange={(e) => {
                                                        setSelectedExistingArea(e.target.value);
                                                        fetchExistingCustomersByArea(e.target.value);
                                                        setSelectedExistingCustomer(null);
                                                        setCustomerDetailsData([]);
                                                    }} 
                                                    disabled={loadingAreas}
                                                    sx={{ 
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': { 
                                                            padding: '6px 8px', 
                                                            fontSize: '0.8rem' 
                                                        }
                                                    }}
                                                >
                                                    {/* <MenuItem key="ALL" value="ALL" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                        ALL
                                                    </MenuItem> */}
                                                    {existingUserAreas.map((area) => ( 
                                                        <MenuItem key={area.AreaId} value={area.AreaId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {area.AreaName}
                                                        </MenuItem> 
                                                    ))}
                                                </Select>
                                                {loadingExistingCustomers && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <Typography variant="body2" sx={{ 
                                                color: colors.textSecondary, 
                                                fontSize: '0.75rem',
                                                textAlign: { xs: 'left', sm: 'center' },
                                                lineHeight: 1.2
                                            }}>
                                                {selectedExistingArea 
                                                    ? `${customersWithSchemes} with schemes, ${customersWithoutSchemes} without schemes` 
                                                    : 'Select area first'
                                                }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={7}>
                                            {existingCustomers.length > 0 && (
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: { xs: 'center', sm: 'flex-end' },
                                                    gap: 0.5
                                                }}>
                                                    <Button
                                                        variant={existingUserMode === 'new-rate' ? 'contained' : 'outlined'}
                                                        onClick={() => {
                                                            setExistingUserMode('new-rate');
                                                            setSelectedCustomersForDelete([]);
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            minWidth: '75px',
                                                            height: '30px',
                                                            px: 1.5,
                                                            fontSize: '0.7rem',
                                                            backgroundColor: existingUserMode === 'new-rate' ? colors.primary : 'transparent',
                                                            borderColor: colors.primary,
                                                            color: existingUserMode === 'new-rate' ? 'white' : colors.primary,
                                                            '&:hover': {
                                                                backgroundColor: existingUserMode === 'new-rate' ? colors.primaryDark : colors.primary + '10'
                                                            }
                                                        }}
                                                        startIcon={<UploadIcon sx={{ fontSize: '14px' }} />}
                                                    >
                                                        New Rate
                                                    </Button>
                                                    <Button
                                                        variant={existingUserMode === 'delete-scheme' ? 'contained' : 'outlined'}
                                                        onClick={() => {
                                                            setExistingUserMode('delete-scheme');
                                                            setSelectedExistingCustomer(null);
                                                            setCustomerDetailsData([]);
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            minWidth: '80px',
                                                            height: '30px',
                                                            px: 1.5,
                                                            fontSize: '0.7rem',
                                                            backgroundColor: existingUserMode === 'delete-scheme' ? colors.error : 'transparent',
                                                            borderColor: colors.error,
                                                            color: existingUserMode === 'delete-scheme' ? 'white' : colors.error,
                                                            '&:hover': {
                                                                backgroundColor: existingUserMode === 'delete-scheme' ? colors.errorDark : colors.error + '10'
                                                            }
                                                        }}
                                                        startIcon={<CloseIcon sx={{ fontSize: '14px' }} />}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Box>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Customer List Header */}
                                {existingCustomers.length > 0 && (
                                    <Box sx={{ mb: 2 }}>

                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    color: colors.textPrimary, 
                                                    fontSize: '0.9rem', 
                                                    fontWeight: 600 
                                                }}>
                                                    {existingUserMode === 'new-rate' ? 'Select Customer for New Rate' : 'Select Customers to Delete'}
                                                </Typography>
                                                {existingUserMode === 'delete-scheme' && selectedCustomersForDelete.length > 0 && (
                                                    <Chip 
                                                        label={`${selectedCustomersForDelete.length} Selected`} 
                                                        size="small" 
                                                        sx={{
                                                            height: '22px',
                                                            fontSize: '0.7rem',
                                                            backgroundColor: colors.error, 
                                                            color: 'white'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <TextField
                                                size="small"
                                                placeholder="Search by ID, Name, Mobile"
                                                value={customerListSearch}
                                                onChange={e => setCustomerListSearch(e.target.value)}
                                                sx={{ 
                                                    width: { xs: '100%', sm: '220px' }, 
                                                    background: colors.backgroundGray, 
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-root': {
                                                        height: '32px',
                                                        fontSize: '0.8rem'
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        padding: '6px 8px'
                                                    }
                                                }}
                                                InputProps={{
                                                  startAdornment: <SearchIcon sx={{ mr: 1, color: colors.textSecondary, fontSize: '16px' }} />
                                                }}
                                            />
                                        </Box>
                                        <Grid container spacing={1.5} sx={{ maxHeight: '320px', overflowY: 'auto', pr: 1 }}>
                                            {filteredExistingCustomers.map((customer, index) => (
                                                <Grid item xs={12} sm={6} md={4} key={customer.CustomerId || index}>
                                                    <Card 
                                                        onClick={() => handleExistingCustomerSelect(customer)}
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            border: (() => {
                                                                if (existingUserMode === 'new-rate') {
                                                                    return selectedExistingCustomer?.CustomerId === customer.CustomerId 
                                                                        ? `2px solid ${colors.success}` 
                                                                        : `1px solid ${colors.border}`;
                                                                } else {
                                                                    const isSelectedForDelete = selectedCustomersForDelete.some(c => c.CustomerId === customer.CustomerId);
                                                                    return isSelectedForDelete 
                                                                        ? `2px solid ${colors.error}` 
                                                                        : `1px solid ${colors.border}`;
                                                                }
                                                            })(),
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: 3
                                                            },
                                                            backgroundColor: (() => {
                                                                if (existingUserMode === 'new-rate') {
                                                                    return selectedExistingCustomer?.CustomerId === customer.CustomerId 
                                                                        ? `${colors.success}08` 
                                                                        : 'rgba(255,255,255,0.8)';
                                                                } else {
                                                                    const isSelectedForDelete = selectedCustomersForDelete.some(c => c.CustomerId === customer.CustomerId);
                                                                    return isSelectedForDelete 
                                                                        ? `${colors.error}08` 
                                                                        : 'rgba(255,255,255,0.8)';
                                                                }
                                                            })(),
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        {(() => {
                                                            if (existingUserMode === 'new-rate') {
                                                                return selectedExistingCustomer?.CustomerId === customer.CustomerId && (
                                                                    <CheckCircleIcon sx={{ 
                                                                        position: 'absolute', 
                                                                        top: 8, 
                                                                        right: 8, 
                                                                        color: colors.success, 
                                                                        fontSize: '20px' 
                                                                    }} />
                                                                );
                                                            } else {
                                                                const isSelectedForDelete = selectedCustomersForDelete.some(c => c.CustomerId === customer.CustomerId);
                                                                return isSelectedForDelete && (
                                                                    <CloseIcon sx={{ 
                                                                        position: 'absolute', 
                                                                        top: 8, 
                                                                        right: 8, 
                                                                        color: colors.error, 
                                                                        fontSize: '20px',
                                                                        backgroundColor: 'white',
                                                                        borderRadius: '50%',
                                                                        padding: '2px'
                                                                    }} />
                                                                );
                                                            }
                                                        })()}
                                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                            {/* Scheme ID as Main Header */}
                                                            <Typography variant="subtitle1" sx={{ 
                                                                display: 'block',
                                                                color: customer.RebateSchemeId ? colors.primary : colors.error,
                                                                mb: 0.8,
                                                                fontSize: '0.9rem',
                                                                fontWeight: 'bold',
                                                                textAlign: 'center',
                                                                backgroundColor: customer.RebateSchemeId ? colors.primary + '10' : colors.error + '10',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                border: `1px solid ${customer.RebateSchemeId ? colors.primary + '30' : colors.error + '30'}`
                                                            }}>
                                                               SCHEME ID : {customer.RebateSchemeId || 'NO SCHEME ASSIGNED'}
                                                            </Typography>

                                                            {/* Customer Name */}
                                                            <Typography variant="subtitle2" noWrap sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                fontWeight: 500,
                                                                mb: 0.5,
                                                                fontSize: '0.75rem'
                                                            }}>
                                                                <PersonOutlineIcon sx={{ mr: 0.5, fontSize: '12px' }} />
                                                                {customer.CustomerName}
                                                            </Typography>
                                                            
                                                            {/* Customer ID */}
                                                            <Typography variant="caption" sx={{ 
                                                                display: 'block', 
                                                                color: colors.textSecondary,
                                                                mb: 0.5,
                                                                fontSize: '0.6rem'
                                                            }}>
                                                                ID: {customer.CustomerId}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                                                <Chip 
                                                                    icon={<PhoneIcon sx={{fontSize: '10px'}} />} 
                                                                    label={customer.Mobile || 'N/A'} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        fontSize: '0.65rem', 
                                                                        backgroundColor: colors.backgroundGray,
                                                                        height: '18px'
                                                                    }} 
                                                                />
                                                                <Typography variant="caption" sx={{ 
                                                                display: 'block', 
                                                                color: colors.textSecondary,
                                                                mb: 0.5,
                                                                fontSize: '0.65rem',
                                                                marginLeft:2
                                                            }}>
                                                                scheme: {customer.RebateSchemeName ? (
                                                                    <Chip
                                                                        label={customer.RebateSchemeName}
                                                                        size="small"
                                                                        sx={{
                                                                            fontSize: '0.6rem',
                                                                            backgroundColor: colors.primary + '15',
                                                                            color: colors.primary,
                                                                            height: '18px',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        onClick={() => handleMapSchemeClick(customer)}
                                                                        sx={{
                                                                            backgroundColor: colors.primary,
                                                                            color: 'white',
                                                                            fontSize: '0.6rem',
                                                                            py: 0.2,
                                                                            px: 0.8,
                                                                            minWidth: 'auto',
                                                                            minHeight: 'auto',
                                                                            height: '18px',
                                                                            borderRadius: '4px',
                                                                            textTransform: 'none',
                                                                            ml: 0.5,
                                                                            '&:hover': {
                                                                                backgroundColor: colors.primaryDark
                                                                            }
                                                                        }}
                                                                    >
                                                                        (Map Scheme)
                                                                    </Button>
                                                                )}
                                                            </Typography>
                                                             
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        {/* Delete Button for Selected Customers */}
                                        {existingUserMode === 'delete-scheme' && selectedCustomersForDelete.length > 0 && (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                mt: 2,
                                                mb: 1.5
                                            }}>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    onClick={handleDeleteSelectedCustomers}
                                                    disabled={isDeleting}
                                                    startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : <CloseIcon sx={{ fontSize: '16px' }} />}
                                                    sx={{
                                                        backgroundColor: colors.error,
                                                        '&:hover': { backgroundColor: colors.errorDark },
                                                        px: 2.5,
                                                        py: 1,
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        height: '36px'
                                                    }}
                                                >
                                                    {isDeleting 
                                                        ? 'Deleting...' 
                                                        : `Delete Schemes (${selectedCustomersForDelete.length})`
                                                    }
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Loading State for Customer Details */}
                                {existingUserMode === 'new-rate' && selectedExistingCustomer && loadingCustomerDetails && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                        <CircularProgress size={40} sx={{ mb: 2, color: colors.primary }} />
                                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                            Loading rate details for {selectedExistingCustomer.CustomerName}...
                                        </Typography>
                                    </Box>
                                )}

                                {/* Customer Details Table with Filter Toggle */}
                                {existingUserMode === 'new-rate' && selectedExistingCustomer && customerDetailsData.length > 0 && !loadingCustomerDetails && (
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                        {/* Filter Toggle Buttons */}
                                      

                                        {/* Rate Details Section */}
                                        {detailsViewFilter === 'rates' && (
                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary }}>
                                                        Rate Details for: {selectedExistingCustomer.CustomerName}
                                                    </Typography>
                                            <TextField
                                                size="small"
                                                placeholder="Search Item Name / Code"
                                                value={rateTableSearch}
                                                onChange={e => setRateTableSearch(e.target.value)}
                                                sx={{ ml: 2, width: 220, background: colors.backgroundGray, borderRadius: 2 }}
                                                InputProps={{
                                                  startAdornment: <SearchIcon sx={{ mr: 1, color: colors.textSecondary }} fontSize="small" />
                                                }}
                                            />
                                            {customerSchemeId && (
                                                <Typography variant="subtitle2" sx={{
                                                  fontSize: '0.9rem',
                                                  fontWeight: 600,
                                                  color: colors.primary,
                                                  background: `${colors.primary}15`,
                                                  px: 2,
                                                  py: 0.5,
                                                  borderRadius: '8px',
                                                  border: `1px solid ${colors.primary}25`
                                                }}>
                                                  Scheme ID: {customerSchemeId}
                                                </Typography>
                                            )}
                                        </Box>
                                        
                                        <TableContainer 
                                            component={Paper} 
                                            elevation={0} 
                                            sx={{ 
                                                ...modernCardStyle,
                                                flex: 1,
                                                overflow: 'auto',
                                                maxHeight: '400px',
                                                background: colors.backgroundCard
                                            }}
                                        >
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow sx={{ '& .MuiTableCell-root': {
                                                        background: colors.backgroundAccent,
                                                        fontWeight: 700, 
                                                        position: 'sticky', 
                                                        top: 0, 
                                                        zIndex: 1,
                                                        color: colors.textPrimary,
                                                        borderBottom: `2px solid ${colors.primary}`,
                                                        py: 2,
                                                        textAlign: 'center',
                                                        fontSize: '0.8rem'
                                                    } }}>
                                                        <TableCell align="center" sx={{ width: 60 }}>S.No.</TableCell>
                                                        <TableCell align="center">Item Name</TableCell>
                                                        <TableCell align="center">Item Code</TableCell>
                                                        <TableCell align="center">Current Qty</TableCell>
                                                        <TableCell align="center">New Qty</TableCell>
                                                        <TableCell align="center">Current Rate</TableCell>
                                                        <TableCell align="center">New Rate</TableCell>
                                                        <TableCell align="center">Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredCustomerDetailsData.map((row) => (
                                                        <TableRow 
                                                            key={row.sno} 
                                                            hover 
                                                            sx={{
                                                                backgroundColor: row.isFromExcel 
                                                                    ? `${colors.info}08` 
                                                                    : row.hasExcelData && row.isChanged 
                                                                        ? `${colors.warning}08` 
                                                                        : 'transparent',
                                                                '&:hover': {
                                                                    backgroundColor: row.isFromExcel 
                                                                        ? `${colors.info}15` 
                                                                        : row.hasExcelData && row.isChanged 
                                                                            ? `${colors.warning}15` 
                                                                            : colors.backgroundGray
                                                                }
                                                            }}
                                                        >
                                                            <TableCell align="center" sx={{ fontWeight: 500, color: colors.textSecondary, fontSize: '0.8rem' }}>
                                                                {row.sno}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                                                {row.itemName}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>
                                                                {row.itemCode}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                                                                {row.qualifyQty}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ 
                                                                fontSize: '0.8rem',
                                                                fontWeight: row.hasExcelData && row.newQty !== row.qualifyQty ? 600 : 'normal',
                                                                color: row.hasExcelData && row.newQty !== row.qualifyQty ? colors.warning : 'inherit'
                                                            }}>
                                                                {row.hasExcelData ? row.newQty : '-'}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                                                {typeof row.rate === 'number' ? `₹${row.rate.toFixed(2)}` : row.rate}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ 
                                                                fontWeight: row.hasExcelData && row.newRate !== row.rate ? 600 : 500,
                                                                color: row.hasExcelData && row.newRate !== row.rate ? colors.warning : colors.success,
                                                                fontSize: '0.8rem'
                                                            }}>
                                                                {row.hasExcelData ? `₹${row.newRate.toFixed(2)}` : '-'}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {row.isFromExcel ? (
                                                                    <Chip 
                                                                        label="New" 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            backgroundColor: colors.info, 
                                                                            color: 'white',
                                                                            fontSize: '0.65rem',
                                                                            height: '20px'
                                                                        }} 
                                                                    />
                                                                ) : row.hasExcelData && row.isChanged ? (
                                                                    <Chip 
                                                                        label="Updated" 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            backgroundColor: colors.warning, 
                                                                            color: 'white',
                                                                            fontSize: '0.65rem',
                                                                            height: '20px'
                                                                        }} 
                                                                    />
                                                                ) : row.hasExcelData ? (
                                                                    <Chip 
                                                                        label="Same" 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            backgroundColor: colors.textTertiary, 
                                                                            color: 'white',
                                                                            fontSize: '0.65rem',
                                                                            height: '20px'
                                                                        }} 
                                                                    />
                                                                ) : (
                                                                    <Chip 
                                                                        label="Current" 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            backgroundColor: colors.borderLight, 
                                                                            color: colors.textSecondary,
                                                                            fontSize: '0.65rem',
                                                                            height: '20px'
                                                                        }} 
                                                                    />
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                            </Box>
                                        )}

                                        {/* Customers List Section */}
                                        {detailsViewFilter === 'customers' && (
                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary }}>
                                                        Customers in Scheme: {customerSchemeId}
                                                    </Typography>
                                                    {selectedCustomersToDelete.length > 0 && (
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={handleDeleteSelectedCustomers}
                                                            size="small"
                                                            sx={{ borderRadius: '8px' }}
                                                        >
                                                            Delete Selected ({selectedCustomersToDelete.length})
                                                        </Button>
                                                    )}
                                                </Box>

                                                {loadingCustomersList ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                                        <CircularProgress size={40} sx={{ color: colors.primary }} />
                                                        <Typography variant="body2" sx={{ mt: 2, color: colors.textSecondary }}>
                                                            Loading customers list...
                                                        </Typography>
                                                    </Box>
                                                ) : customersListData.length > 0 ? (
                                                    <TableContainer 
                                                        component={Paper} 
                                                        elevation={0} 
                                                        sx={{ 
                                                            ...modernCardStyle,
                                                            flex: 1,
                                                            overflow: 'auto',
                                                            maxHeight: '400px',
                                                            background: colors.backgroundCard
                                                        }}
                                                    >
                                                        <Table stickyHeader size="small">
                                                            <TableHead>
                                                                <TableRow sx={{ '& .MuiTableCell-root': {
                                                                    background: colors.backgroundAccent,
                                                                    fontWeight: 700, 
                                                                    position: 'sticky', 
                                                                    top: 0, 
                                                                    zIndex: 1,
                                                                    color: colors.textPrimary,
                                                                    borderBottom: `2px solid ${colors.primary}`,
                                                                    py: 2,
                                                                    textAlign: 'center',
                                                                    fontSize: '0.8rem'
                                                                } }}>
                                                                    <TableCell align="center" sx={{ width: 50 }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedCustomersToDelete.length === customersListData.length && customersListData.length > 0}
                                                                            onChange={(e) => handleSelectAllCustomers(e.target.checked)}
                                                                            style={{ cursor: 'pointer' }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="center">Customer Name</TableCell>
                                                                    <TableCell align="center">Area Name</TableCell>
                                                                    <TableCell align="center">Rebate Scheme Name</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {customersListData.map((customer, index) => (
                                                                    <TableRow 
                                                                        key={customer.CustomerId} 
                                                                        hover 
                                                                        sx={{
                                                                            backgroundColor: selectedCustomersToDelete.includes(customer.CustomerId) 
                                                                                ? `${colors.error}08` 
                                                                                : 'transparent',
                                                                            '&:hover': {
                                                                                backgroundColor: selectedCustomersToDelete.includes(customer.CustomerId)
                                                                                    ? `${colors.error}15`
                                                                                    : colors.backgroundGray
                                                                            }
                                                                        }}
                                                                    >
                                                                        <TableCell align="center">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedCustomersToDelete.includes(customer.CustomerId)}
                                                                                onChange={(e) => handleCustomerSelection(customer.CustomerId, e.target.checked)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                                                            {customer.CustomerName}
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>
                                                                            {customer.AreaName}
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>
                                                                            {customer.RebateSchemeName}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                ) : (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                                        <PeopleIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 1 }} />
                                                        <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 1 }}>
                                                            No Customers Found
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: colors.textTertiary, textAlign: 'center' }}>
                                                            No customers are currently assigned to this rebate scheme.
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Excel Upload Section for Existing User */}
                                {selectedExistingCustomer && (customerDetailsData.length > 0 || (!loadingCustomerDetails && customerDetailsData.length === 0)) && (
                                    <Box sx={{ mt: 3, mb: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, color: colors.textPrimary, display: 'flex', alignItems: 'center' }}>
                                            <UploadIcon sx={{ mr: 1, color: colors.primary }} />
                                            Upload New Rates
                                        </Typography>
                                        
                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                                    <Button 
                                                        variant="contained" 
                                                        component="label" 
                                                        size="small" 
                                                        sx={{ 
                                                            ...gradientButtonStyle,
                                                            fontSize: '0.875rem',
                                                            py: 1,
                                                            px: 2.5,
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        Choose Excel File
                                                        <input 
                                                            type="file" 
                                                            accept=".xlsx,.xls" 
                                                            hidden 
                                                            onChange={handleExistingUserFileChange} 
                                                            ref={existingUserFileInputRef} 
                                                        />
                                                    </Button>
                                                    <Button 
                                                        variant="outlined" 
                                                        size="small" 
                                                        onClick={customerDetailsData.length > 0 ? processExistingUserExcel : processExistingUserExcelForNewCustomer} 
                                                        disabled={!existingUserExcelFile || isExistingUserExcelProcessing}
                                                        sx={{ 
                                                            borderColor: colors.primary, 
                                                            color: colors.primary,
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            px: 2.5,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                borderColor: colors.primaryDark,
                                                                background: `${colors.primary}10`,
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: colors.shadow
                                                            }
                                                        }}
                                                    >
                                                        {isExistingUserExcelProcessing ? <CircularProgress size={16} /> : 
                                                         customerDetailsData.length > 0 ? 'Validate & Compare' : 'Upload Excel'}
                                                    </Button>
                                                    {existingUserExcelFile && (
                                                        <Button 
                                                            variant="outlined" 
                                                            size="small" 
                                                            color="error" 
                                                            onClick={clearExistingUserExcel}
                                                            sx={{ 
                                                                borderColor: colors.error, 
                                                                color: colors.error,
                                                                borderRadius: '12px',
                                                                fontWeight: 600,
                                                                textTransform: 'none',
                                                                py: 1,
                                                                px: 2.5,
                                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                '&:hover': {
                                                                    borderColor: colors.errorDark,
                                                                    backgroundColor: `${colors.error}10`,
                                                                    transform: 'translateY(-1px)',
                                                                    boxShadow: colors.shadow
                                                                }
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    )}
                                                </Box>
                                                {existingUserExcelFileName && (
                                                    <Chip 
                                                        label={existingUserExcelFileName} 
                                                        size="small" 
                                                        sx={{ 
                                                            mt: 1,
                                                            background: colors.successGradient,
                                                            color: colors.white,
                                                            fontWeight: 500,
                                                            borderRadius: '8px',
                                                            '& .MuiChip-label': {
                                                                fontSize: '0.75rem'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Button 
                                                    variant="contained" 
                                                    onClick={handleExistingUserUpload} 
                                                    disabled={existingUserValidationErrors.length > 0 || existingUserExcelData.length === 0 || isExistingUserUploading}
                                                    fullWidth
                                                    sx={{ 
                                                        background: colors.successGradient,
                                                        color: colors.white,
                                                        borderRadius: '12px',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        py: 1.5,
                                                        boxShadow: colors.shadowMd,
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        '&:hover': {
                                                            background: `linear-gradient(135deg, ${colors.successLight} 0%, ${colors.successDark} 100%)`,
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: colors.shadowLg
                                                        },
                                                        '&:disabled': { 
                                                            background: colors.borderLight,
                                                            color: colors.textTertiary,
                                                            transform: 'none',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                    startIcon={isExistingUserUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                                                >
                                                    {isExistingUserUploading ? 'Uploading...' : 'Upload Rate Updates'}
                                                </Button>
                                                {existingUserValidationErrors.length > 0 && (
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.error }}>
                                                        Fix {existingUserValidationErrors.length} errors before upload
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                        
                                        <Typography variant="caption" sx={{ display: 'block', color: colors.textSecondary }}>
                                            Required columns: ItemCode, Rate, QualifyQty
                                        </Typography>
                                    </Box>
                                )}

                                {/* Validation Errors for Existing User */}
                                {existingUserValidationErrors.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                                            Found {existingUserValidationErrors.length} validation error{existingUserValidationErrors.length > 1 ? 's' : ''}. Please fix these issues in your Excel file:
                                        </Alert>
                                        <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto', backgroundColor: '#fef2f2', border: `1px solid ${colors.error}` }}>
                                            {existingUserValidationErrors.map((error, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
                                                    <Box sx={{ 
                                                        minWidth: 24, 
                                                        height: 24, 
                                                        backgroundColor: colors.error, 
                                                        color: 'white', 
                                                        borderRadius: '50%', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center', 
                                                        mr: 2, 
                                                        fontSize: '12px', 
                                                        fontWeight: 'bold' 
                                                    }}>
                                                        {index + 1}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: colors.errorDark, fontWeight: 500 }}>
                                                        {error}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Paper>
                                    </Box>
                                )}


                                {/* No Rate Details Found */}
                                {selectedExistingCustomer && customerDetailsData.length === 0 && !loadingCustomerDetails && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                        <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 1 }}>
                                            No Rate Details Found
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', mb: 2 }}>
                                            No rate scheme details are available for {selectedExistingCustomer.CustomerName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.primary, textAlign: 'center', fontWeight: 600 }}>
                                            ✨ Upload an Excel file above and click "Upload Excel" to create rates for this customer!
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}



                        {userFilterType === 'multiple' && (
                            <>
                                {/* Multiple User - Area and Customer Selection */}
                                <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                    <Grid container spacing={1.5} alignItems="center">
                                        {/* Area Dropdown */}
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Select Area</InputLabel>
                                                <Select
                                                    value={selectedMultipleArea}
                                                    label="Select Area"
                                                    onChange={(e) => {
                                                        setSelectedMultipleArea(e.target.value);
                                                        fetchRebateSchemes(e.target.value);
                                                    }}
                                                    disabled={loadingMultipleAreas}
                                                    sx={{
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        }
                                                    }}
                                                >
                                                    {multipleUserAreas.map((area) => (
                                                        <MenuItem key={area.AreaId} value={area.AreaId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {area.AreaName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Rebate Scheme Dropdown */}
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Rebate Scheme</InputLabel>
                                                <Select
                                                    value={selectedRebateScheme}
                                                    label="Rebate Scheme"
                                                    onChange={(e) => setSelectedRebateScheme(e.target.value)}
                                                    disabled={loadingRebateSchemes}
                                                    sx={{
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        }
                                                    }}
                                                >
                                                    {rebateSchemes.map((scheme) => (
                                                        <MenuItem key={scheme.RebateSchemeId} value={scheme.RebateSchemeId} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {scheme.RebateSchemeName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Customer Type Dropdown */}
                                        <Grid item xs={12} sm={3} md={2.5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.8rem' }}>Customer Type</InputLabel>
                                                <Select
                                                    value={selectedCustomerType}
                                                    label="Customer Type"
                                                    onChange={(e) => setSelectedCustomerType(e.target.value)}
                                                    disabled={loadingCustomerTypes}
                                                    sx={{
                                                        height: '32px',
                                                        fontSize: '0.8rem',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 8px',
                                                            fontSize: '0.8rem'
                                                        }
                                                    }}
                                                >
                                                    {customerTypes.map((type) => (
                                                        <MenuItem key={type.customerTypeCode} value={type.customerTypeCode} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                                            {type.customerTypeName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Search Controls */}
                                <Box sx={{ flexShrink: 0, mb: 1.5 }}>
                                    <Grid container spacing={1.5} alignItems="end">
                                        {/* Search By Radio Group */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                                                Search By:
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Button
                                                    size="small"
                                                    onClick={() => setMultipleSearchType('CUSTCODE')}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        minWidth: 'auto',
                                                        borderRadius: '6px',
                                                        background: multipleSearchType === 'CUSTCODE' ? colors.primaryGradient : 'transparent',
                                                        color: multipleSearchType === 'CUSTCODE' ? colors.white : colors.textSecondary,
                                                        border: `1px solid ${multipleSearchType === 'CUSTCODE' ? 'transparent' : colors.border}`,
                                                        '&:hover': {
                                                            backgroundColor: multipleSearchType === 'CUSTCODE' ? '' : `${colors.primary}10`
                                                        }
                                                    }}
                                                >
                                                    Customer Code
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => setMultipleSearchType('MOBILE')}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        minWidth: 'auto',
                                                        borderRadius: '6px',
                                                        background: multipleSearchType === 'MOBILE' ? colors.primaryGradient : 'transparent',
                                                        color: multipleSearchType === 'MOBILE' ? colors.white : colors.textSecondary,
                                                        border: `1px solid ${multipleSearchType === 'MOBILE' ? 'transparent' : colors.border}`,
                                                        '&:hover': {
                                                            backgroundColor: multipleSearchType === 'MOBILE' ? '' : `${colors.primary}10`
                                                        }
                                                    }}
                                                >
                                                    Mobile
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => setMultipleSearchType('CUSTNAME')}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        minWidth: 'auto',
                                                        borderRadius: '6px',
                                                        background: multipleSearchType === 'CUSTNAME' ? colors.primaryGradient : 'transparent',
                                                        color: multipleSearchType === 'CUSTNAME' ? colors.white : colors.textSecondary,
                                                        border: `1px solid ${multipleSearchType === 'CUSTNAME' ? 'transparent' : colors.border}`,
                                                        '&:hover': {
                                                            backgroundColor: multipleSearchType === 'CUSTNAME' ? '' : `${colors.primary}10`
                                                        }
                                                    }}
                                                >
                                                    Customer Name
                                                </Button>
                                            </Box>
                                        </Grid>

                                        {/* Search Input and Button */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={multipleSearchInput}
                                                    onChange={(e) => setMultipleSearchInput(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleMultipleCustomerSearch()}
                                                    placeholder={multipleSearchType === 'CUSTCODE' ? 'Enter Customer Code' : 
                                                               multipleSearchType === 'MOBILE' ? 'Enter Mobile Number' : 
                                                               'Enter Customer Name'}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '32px',
                                                            fontSize: '0.8rem',
                                                            '& input': {
                                                                padding: '6px 8px',
                                                                fontSize: '0.8rem'
                                                            }
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleMultipleCustomerSearch}
                                                    disabled={loadingMultipleSearch || !selectedCustomerType || !multipleSearchInput.trim()}
                                                    sx={{
                                                        minWidth: '40px',
                                                        px: 1.5,
                                                        fontSize: '0.7rem',
                                                        borderRadius: '6px',
                                                        background: colors.primaryGradient,
                                                        fontWeight: 600,
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    {loadingMultipleSearch ? <CircularProgress size={14} color="inherit" /> : <SearchIcon sx={{ fontSize: '14px' }} />}
                                                </Button>
                                            </Box>
                                        </Grid>

                                        {/* Selected Count */}
                                        <Grid item xs={12} sm={12} md={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                                {multipleSelectedCustomers.length > 0 && (
                                                    <Chip
                                                        label={`${multipleSelectedCustomers.length} selected`}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ fontSize: '0.7rem', height: '24px' }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Search Results */}
                                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                    <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                                        <SearchIcon sx={{ mr: 1, fontSize: '16px', color: colors.primary }} />
                                        Search Results ({multipleSearchResults.length})
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        maxHeight: '400px', 
                                        overflowY: 'auto',
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        p: 1
                                    }}>
                                        {multipleSearchResults.length > 0 ? (
                                            <Grid container spacing={1.5}>
                                                {multipleSearchResults.map((customer, index) => {
                                                    const customerId = customer.custID || customer.customerCode;
                                                    const isSelected = multipleSelectedCustomers.some(c => (c.custID || c.customerCode) === customerId);
                                                    return (
                                                        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                                            <Fade in={true} timeout={300 + (index * 30)}>
                                                                <Card
                                                                    onClick={() => handleMultipleCustomerSelect(customer)}
                                                                    sx={{
                                                                        borderRadius: '8px',
                                                                        backgroundColor: isSelected ? '#f0fdf4' : colors.backgroundCard,
                                                                        border: isSelected ? `2px solid ${colors.success}` : `1px solid ${colors.border}`,
                                                                        transition: 'all 0.2s ease',
                                                                        cursor: 'pointer',
                                                                        position: 'relative',
                                                                        '&:hover': {
                                                                            transform: 'translateY(-1px)',
                                                                            boxShadow: colors.shadowMd,
                                                                            borderColor: isSelected ? colors.success : colors.primary
                                                                        },
                                                                        '&::before': isSelected ? {
                                                                            content: '"✓"',
                                                                            position: 'absolute',
                                                                            top: '4px',
                                                                            right: '4px',
                                                                            width: '16px',
                                                                            height: '16px',
                                                                            backgroundColor: colors.success,
                                                                            color: 'white',
                                                                            borderRadius: '50%',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '10px',
                                                                            fontWeight: 'bold',
                                                                            zIndex: 1
                                                                        } : {}
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                                fontSize: '0.8rem',
                                                                                mb: 0.5,
                                                                                color: colors.textPrimary,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                lineHeight: 1.2
                                                                            }}
                                                                        >
                                                                            <PersonOutlineIcon sx={{ mr: 0.5, fontSize: '12px', color: isSelected ? colors.success : colors.primary }} />
                                                                            {customer.customerName || 'N/A'}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                                            <Chip
                                                                                icon={<PhoneIcon sx={{ fontSize: '8px' }} />}
                                                                                label={customer.mobile || 'N/A'}
                                                                                variant="outlined"
                                                                                size="small"
                                                                                sx={{
                                                                                    fontSize: '0.65rem',
                                                                                    height: '16px',
                                                                                    fontWeight: 500,
                                                                                    borderColor: colors.success,
                                                                                    color: colors.successDark,
                                                                                    backgroundColor: '#ecfdf5',
                                                                                    '& .MuiChip-icon': {
                                                                                        fontSize: '6px',
                                                                                        color: colors.successDark
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <Chip
                                                                                label={customerId || 'N/A'}
                                                                                variant="outlined"
                                                                                size="small"
                                                                                sx={{
                                                                                    fontSize: '0.65rem',
                                                                                    height: '16px',
                                                                                    fontWeight: 500,
                                                                                    borderColor: colors.info,
                                                                                    color: colors.infoDark,
                                                                                    backgroundColor: '#eff6ff'
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Fade>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        ) : multipleSearchInput && !loadingMultipleSearch ? (
                                            <Box sx={{ 
                                                textAlign: 'center', 
                                                py: 4, 
                                                color: colors.textSecondary,
                                                border: `1px dashed ${colors.borderDark}`,
                                                borderRadius: '8px',
                                                backgroundColor: colors.backgroundGray
                                            }}>
                                                <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 0.5 }}>
                                                    No customers found
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                    Try adjusting your search criteria
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ 
                                                textAlign: 'center', 
                                                py: 4, 
                                                color: colors.textSecondary,
                                                border: `1px dashed ${colors.borderDark}`,
                                                borderRadius: '8px',
                                                backgroundColor: colors.backgroundGray
                                            }}>
                                                <SearchIcon sx={{ fontSize: '40px', color: colors.borderDark, mb: 2 }} />
                                                <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 0.5 }}>
                                                    Ready to search
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                    Select area, rebate scheme, customer type and search for customers
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </>
                        )}

                        {/* Selected Customers Display for Multiple User */}
                        {userFilterType === 'multiple' && multipleSelectedCustomers.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center' }}>
                                        <GroupIcon sx={{ mr: 1, fontSize: '16px', color: colors.primary }} />
                                        Selected Customers ({multipleSelectedCustomers.length})
                                    </Typography>
                                    
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        onClick={handleSaveMultipleCustomers}
                                        disabled={!selectedRebateScheme || savingMultipleCustomers}
                                        sx={{
                                            fontSize: '0.75rem',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: colors.success,
                                            '&:hover': {
                                                backgroundColor: colors.successDark
                                            }
                                        }}
                                    >
                                        {savingMultipleCustomers ? (
                                            <>
                                                <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />
                                                Saving...
                                            </>
                                        ) : (
                                            `Save Selected (${multipleSelectedCustomers.length})`
                                        )}
                                    </Button>
                                </Box>
                                
                                <Box sx={{ 
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '8px',
                                    p: 2,
                                    backgroundColor: colors.backgroundCard,
                                    maxHeight: '120px',
                                    overflowY: 'auto'
                                }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {multipleSelectedCustomers.map((customer, index) => {
                                            const customerId = customer.custID || customer.customerCode;
                                            return (
                                                <Chip
                                                    key={index}
                                                    label={customer.customerName || 'N/A'}
                                                    onDelete={() => handleMultipleCustomerSelect(customer)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: customer.mappingStatus === 'Mapped' ? colors.success : 
                                                                        customer.needsMapping ? colors.warning : colors.primary,
                                                        color: 'white',
                                                        fontSize: '0.75rem',
                                                        height: '24px',
                                                        fontWeight: 500,
                                                        '& .MuiChip-deleteIcon': {
                                                            color: 'white',
                                                            '&:hover': {
                                                                color: 'rgba(255,255,255,0.8)'
                                                            }
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                    
                                    {/* Show customers that need mapping */}
                                    {multipleSelectedCustomers.some(c => c.needsMapping) && (
                                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: colors.textSecondary, mb: 1, display: 'block' }}>
                                                Customers needing area mapping:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {multipleSelectedCustomers.filter(c => c.needsMapping).map((customer, index) => (
                                                    <Button
                                                        key={index}
                                                        size="small"
                                                        variant="contained"
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            height: '20px',
                                                            minWidth: 'auto',
                                                            px: 1,
                                                            backgroundColor: colors.primary,
                                                            '&:hover': {
                                                                backgroundColor: colors.primaryDark
                                                            }
                                                        }}
                                                        onClick={() => handleMapCustomer(customer)}
                                                    >
                                                        Map {customer.customerName}
                                                    </Button>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Master View (if needed in future) */}
                        {activeView === 'master' && (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {/* Master Upload Controls */}
                                <Box sx={{ flexShrink: 0, mb: 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={8}>
                                            <StepBox title="1. Import Excel File" icon={<UploadIcon sx={{color: colors.primary}}/>}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                                                    <Button 
                                                        variant="contained" 
                                                        component="label" 
                                                        size="small" 
                                                        sx={{ 
                                                            ...gradientButtonStyle,
                                                            fontSize: '0.875rem',
                                                            py: 1,
                                                            px: 2.5,
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        Choose File
                                                        <input 
                                                            type="file" 
                                                            accept=".xlsx,.xls" 
                                                            hidden 
                                                            onChange={handleMasterFileChange} 
                                                            ref={masterFileInputRef} 
                                                        />
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={handleDownloadMasterSample}
                                                        sx={{
                                                            borderColor: '#4caf50',
                                                            color: '#4caf50',
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            px: 2.5,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                borderColor: '#388e3c',
                                                                background: 'rgba(76, 175, 80, 0.04)',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: colors.shadow
                                                            }
                                                        }}
                                                    >
                                                        Download Sample
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={processMasterExcel} 
                                                        disabled={!masterExcelFile || isMasterExcelProcessing}
                                                        sx={{ 
                                                            borderColor: colors.primary, 
                                                            color: colors.primary,
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            px: 2.5,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                borderColor: colors.primaryDark,
                                                                background: `${colors.primary}10`,
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: colors.shadow
                                                            }
                                                        }}
                                                    >
                                                        {isMasterExcelProcessing ? <CircularProgress size={16} /> : 'Validate'}
                                                    </Button>
                                                    {masterExcelFile && (
                                                        <Button 
                                                            variant="outlined" 
                                                            size="small" 
                                                            color="error" 
                                                            onClick={clearMasterExcel}
                                                            sx={{ 
                                                                borderColor: colors.error, 
                                                                color: colors.error,
                                                                borderRadius: '12px',
                                                                fontWeight: 600,
                                                                textTransform: 'none',
                                                                py: 1,
                                                                px: 2.5,
                                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                '&:hover': {
                                                                    borderColor: colors.errorDark,
                                                                    backgroundColor: `${colors.error}10`,
                                                                    transform: 'translateY(-1px)',
                                                                    boxShadow: colors.shadow
                                                                }
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    )}
                                                </Box>
                                                {masterExcelFileName && (
                                                    <Chip 
                                                        label={masterExcelFileName} 
                                                        size="small" 
                                                        sx={{ 
                                                            background: colors.successGradient,
                                                            color: colors.white,
                                                            fontWeight: 500,
                                                            borderRadius: '8px',
                                                            '& .MuiChip-label': {
                                                                fontSize: '0.75rem'
                                                            }
                                                        }}
                                                    />
                                                )}
                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.textSecondary }}>
                                                    Required columns: ItemCode, Rate, QualifyQty
                                                </Typography>
                                            </StepBox>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <StepBox title="2. Upload Data" icon={<CheckCircleIcon sx={{color: colors.success}}/>}>
                                                <Button 
                                                    variant="contained" 
                                                    onClick={handleMasterUpload} 
                                                    disabled={masterValidationErrors.length > 0 || masterExcelData.length === 0 || isMasterUploading}
                                                    fullWidth
                                                    sx={{ 
                                                        background: colors.successGradient,
                                                        color: colors.white,
                                                        borderRadius: '12px',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        py: 1.5,
                                                        boxShadow: colors.shadowMd,
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        '&:hover': {
                                                            background: `linear-gradient(135deg, ${colors.successLight} 0%, ${colors.successDark} 100%)`,
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: colors.shadowLg
                                                        },
                                                        '&:disabled': { 
                                                            background: colors.borderLight,
                                                            color: colors.textTertiary,
                                                            transform: 'none',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                    startIcon={isMasterUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                                                >
                                                    {isMasterUploading ? 'Uploading...' : 'Upload Master Data'}
                                                </Button>
                                                {masterValidationErrors.length > 0 && (
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.error }}>
                                                        Fix {masterValidationErrors.length} errors before upload
                                                    </Typography>
                                                )}

                                                {/* Customer Mapping Button */}
                                                {masterUnmappedCustomers.length > 0 && (
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => setShowMasterMappingDialog(true)}
                                                        fullWidth
                                                        sx={{
                                                            mt: 2,
                                                            borderColor: colors.warning,
                                                            color: colors.warning,
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            '&:hover': {
                                                                borderColor: colors.warningDark,
                                                                backgroundColor: `${colors.warning}10`,
                                                                color: colors.warningDark
                                                            }
                                                        }}
                                                        startIcon={<PersonAddIcon />}
                                                    >
                                                        Map {masterUnmappedCustomers.length} Customer{masterUnmappedCustomers.length !== 1 ? 's' : ''}
                                                    </Button>
                                                )}
                                            </StepBox>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Validation Errors Alert */}
                                {masterValidationErrors.length > 0 && (
                                    <Box sx={{ mb: 2, flexShrink: 0 }}>
                                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                                            Found {masterValidationErrors.length} validation error{masterValidationErrors.length > 1 ? 's' : ''} in {Object.keys(masterRowErrors).length} row{Object.keys(masterRowErrors).length > 1 ? 's' : ''}. Please fix these issues:
                                        </Alert>
                                        <Paper sx={{ p: 2, maxHeight: 250, overflow: 'auto', backgroundColor: '#fef2f2', border: `1px solid ${colors.error}` }}>
                                            {masterValidationErrors.map((error, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
                                                    <Box sx={{ 
                                                        minWidth: 24, 
                                                        height: 24, 
                                                        backgroundColor: colors.error, 
                                                        color: 'white', 
                                                        borderRadius: '50%', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center', 
                                                        mr: 2, 
                                                        fontSize: '12px', 
                                                        fontWeight: 'bold' 
                                                    }}>
                                                        {index + 1}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: colors.errorDark, fontWeight: 500 }}>
                                                        {error}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Paper>
                                    </Box>
                                )}

                                {/* Data Table */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FactCheckIcon sx={{ mr: 1.5, color: colors.success, fontSize: '28px' }} />
                                            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary }}>
                                                Data Preview & Validation
                                            </Typography>
                                        </Box>
                                        {masterExcelData.length > 0 && (
                                            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                                {masterExcelData.length} rows • {masterExcelData.filter(row => row.hasError).length} errors
                                            </Typography>
                                        )}
                                    </Box>

                                    <TableContainer 
                                        component={Paper} 
                                        elevation={0} 
                                        sx={{ 
                                            ...modernCardStyle,
                                            flex: 1,
                                            overflow: 'auto',
                                            maxHeight: '100%',
                                            background: colors.backgroundCard
                                        }}
                                    >
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow sx={{ '& .MuiTableCell-root': {
                                                    background: colors.backgroundAccent,
                                                    fontWeight: 700, 
                                                    position: 'sticky', 
                                                    top: 0, 
                                                    zIndex: 1,
                                                    color: colors.textPrimary,
                                                    borderBottom: `2px solid ${colors.primary}`,
                                                    py: 2,
                                                    textAlign: 'center'
                                                } }}>
                                                    <TableCell align="center" sx={{ width: 80 }}>Row</TableCell>
                                                    <TableCell align="center">Item Code</TableCell>
                                                    <TableCell align="center">Qualify Qty</TableCell>
                                                    <TableCell align="center">Rate</TableCell>
                                                    <TableCell align="center" sx={{ width: 100 }}>Status</TableCell>
                                                    <TableCell align="center">Errors</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {masterExcelData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ p: 4, color: colors.textSecondary }}>
                                                            {masterExcelFile ? 'Click "Validate" to process the Excel file' : 'Import an Excel file to see data preview'}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    masterExcelData.map((row, index) => (
                                                        <TableRow 
                                                            key={index} 
                                                            hover 
                                                            sx={{
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': {
                                                                    backgroundColor: row.hasError 
                                                                        ? 'rgba(244, 67, 54, 0.08)' 
                                                                        : colors.backgroundGray,
                                                                    transform: 'scale(1.005)'
                                                                },
                                                                backgroundColor: row.hasError 
                                                                    ? 'rgba(244, 67, 54, 0.03)' 
                                                                    : 'transparent',
                                                                borderLeft: row.hasError 
                                                                    ? `4px solid ${colors.error}` 
                                                                    : '4px solid transparent'
                                                            }}
                                                        >
                                                            <TableCell align="center" sx={{ 
                                                                fontWeight: 500, 
                                                                color: row.hasError ? colors.error : colors.textSecondary 
                                                            }}>
                                                                {row.rowNumber}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: row.hasError && row.errors.includes('ItemCode missing') ? colors.error : 'inherit' }}>
                                                                {row.ItemCode || <span style={{color: colors.textSecondary, fontStyle: 'italic'}}>Missing</span>}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: row.hasError && (row.errors.includes('Invalid Quantity') || row.errors.includes('QualifyQty-Rate Mismatch')) ? colors.error : 'inherit' }}>
                                                                {isNaN(row.QualifyQty) ? <span style={{color: colors.error}}>Invalid</span> : row.QualifyQty}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: row.hasError && (row.errors.includes('Invalid Rate') || row.errors.includes('Rate-QualifyQty Mismatch')) ? colors.error : 'inherit' }}>
                                                                {isNaN(row.Rate) ? <span style={{color: colors.error}}>Invalid</span> : row.Rate.toFixed(2)}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {row.hasError ? (
                                                                    <Chip 
                                                                        label="Error" 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            backgroundColor: colors.error, 
                                                                            color: 'white',
                                                                            fontSize: '0.7rem'
                                                                        }} 
                                                                    />
                                                                ) : (
                                                                    <Chip 
                                                                        label="Valid" 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            backgroundColor: colors.success, 
                                                                            color: 'white',
                                                                            fontSize: '0.7rem'
                                                                        }} 
                                                                    />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ fontSize: '0.75rem', color: colors.error }}>
                                                                {row.errors.length > 0 ? row.errors.join(', ') : '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                        )}
                       
                        {/* Scrollable Content Area - Only for New User View */}
                        {userFilterType === 'new' && selectedCustomers.length > 0 && (
                            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                                {/* <Divider sx={{ my: 2.5, borderColor: colors.border, flexShrink: 0 }} /> */}
                                
                                {/* Three Step Boxes - Fixed Height */}
                                <Box sx={{ flexShrink: 0, mb: 2 }}>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} lg={4}>
                                            <StepBox title={`1. Customer Selected`} icon={<GroupIcon sx={{color: colors.primary}}/>}>
                                                <Box sx={{ maxHeight: 100, overflowY: 'auto', p: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selectedCustomers.map(c => (
                                                        <Chip 
                                                            key={c.custID || c.customerCode} 
                                                            label={c.customerName} 
                                                            size="small" 
                                                            onDelete={() => handleCustomerSelect(c)} 
                                                            sx={{
                                                                backgroundColor: 'rgba(230,230,230,0.7)',
                                                                height: '20px',
                                                                fontSize: '0.7rem',
                                                                '& .MuiChip-deleteIcon': { fontSize: '14px' }
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </StepBox>
                                        </Grid>
                                        {/* <Grid item xs={12} lg={4}>
                                            <StepBox title="2. Choose Scheme Option" icon={<RuleIcon sx={{color: colors.primary}}/>}>
                                                <RadioGroup value={schemeChoice} onChange={(e) => setSchemeChoice(e.target.value)}>
                                                    <FormControlLabel value="create" control={<Radio />} label="Create New Rate Scheme" />
                                                    <FormControlLabel value="existing" control={<Radio />} label="Use Existing Rate Scheme" />
                                                </RadioGroup>
                                            </StepBox>
                                        </Grid> */}
                                        <Grid item xs={12} lg={4}>
                                            <StepBox title="2. Upload Rate Details" icon={<PlaylistAddCheckIcon sx={{color: colors.primary}}/>}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                        <Button
                                                            variant="contained"
                                                            component="label"
                                                            size="small"
                                                            sx={{
                                                                textTransform: 'none',
                                                                flexShrink: 0,
                                                                backgroundColor: colors.primary,
                                                                fontSize: '0.75rem',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                height: '28px'
                                                            }}
                                                        >
                                                            Choose File
                                                            <input type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} ref={fileInputRef} />
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={processExcelForNewScheme}
                                                            disabled={!excelFile || isExcelProcessing}
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                height: '28px'
                                                            }}
                                                        >
                                                            {isExcelProcessing ? <CircularProgress size={14} /> : 'Validate'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<DownloadIcon sx={{fontSize: '16px'}} />}
                                                            onClick={downloadSampleExcel}
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                height: '28px',
                                                                borderColor: colors.success,
                                                                color: colors.success,
                                                                '&:hover': {
                                                                    borderColor: colors.successDark,
                                                                    backgroundColor: `${colors.success}10`
                                                                }
                                                            }}
                                                        >
                                                            Sample
                                                        </Button>
                                                        {excelFile && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                color="error"
                                                                onClick={clearNewUserExcel}
                                                                sx={{
                                                                    borderColor: colors.error,
                                                                    color: colors.error,
                                                                    fontSize: '0.75rem',
                                                                    px: 1.5,
                                                                    py: 0.5,
                                                                    height: '28px',
                                                                    '&:hover': {
                                                                        borderColor: colors.errorDark,
                                                                        backgroundColor: `${colors.error}10`
                                                                    }
                                                                }}
                                                            >
                                                                Clear
                                                            </Button>
                                                        )}
                                                    </Box>
                                                    {/* <FormControl fullWidth size="small">
                                                        <InputLabel>Select Existing Scheme</InputLabel>
                                                        <Select value={selectedRateScheme} label="Select Existing Scheme" onChange={(e) => handleSelectExistingScheme(e.target.value)} disabled={loadingRateSchemes || loadingSchemeDetails}>
                                                            {rateSchemes.map((scheme) => (
                                                                <MenuItem key={scheme.SchemeId} value={scheme.SchemeId}>{scheme.SchemeName}</MenuItem>
                                                            ))}
                                                        </Select>
                                                        {(loadingRateSchemes || loadingSchemeDetails) && <CircularProgress size={20} sx={{ mt: 1 }} />}
                                                    </FormControl>
                                                )} */}
                                            </StepBox>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Review & Upload Section - Flexible Height */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <FactCheckIcon sx={{ mr: 1, color: colors.success, fontSize: '20px' }} />
                                            <Box>
                                                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>3. Review & Upload</Typography>
                                                <Typography variant="caption" sx={{ color: colors.textSecondary, fontStyle: 'italic', fontSize: '0.7rem' }}>
                                                    Validated imported Excel data
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Button 
                                            variant="contained" 
                                            onClick={schemeChoice === 'create' ? handleUploadNewScheme : handleUploadExistingScheme} 
                                            disabled={tableData.length === 0 || loadingMapping || (schemeChoice === 'create' && excelValidationErrors.length > 0)} 
                                            size="small"
                                            sx={{ 
                                                textTransform: 'none', 
                                                backgroundColor: colors.success, 
                                                '&:hover': { backgroundColor: colors.successDark }, 
                                                fontSize: '0.8rem',
                                                px: 2, 
                                                py: 1,
                                                height: '32px'
                                            }} 
                                            startIcon={loadingMapping ? <CircularProgress size={14} color="inherit" /> : <UploadIcon sx={{ fontSize: '16px' }} />}
                                        >
                                            Upload to Customers
                                        </Button>
                                    </Box>
                                    {excelValidationErrors.length > 0 && (
                                        <Box sx={{ mb: 1.5, flexShrink: 0 }}>
                                            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                                                Found {excelValidationErrors.length} validation error{excelValidationErrors.length > 1 ? 's' : ''}. Please fix these issues in your Excel file:
                                            </Alert>
                                            <Paper sx={{ p: 2, maxHeight: 250, overflow: 'auto', backgroundColor: '#fef2f2', border: `1px solid ${colors.error}` }}>
                                                {excelValidationErrors.map((error, index) => (
                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, '&:last-child': { mb: 0 } }}>
                                                        <Box sx={{ 
                                                            minWidth: 24, 
                                                            height: 24, 
                                                            backgroundColor: colors.error, 
                                                            color: 'white', 
                                                            borderRadius: '50%', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center', 
                                                            mr: 2, 
                                                            fontSize: '12px', 
                                                            fontWeight: 'bold' 
                                                        }}>
                                                            {index + 1}
                                                        </Box>
                                                        <Typography variant="body2" sx={{ color: colors.errorDark, fontWeight: 500 }}>
                                                            {error}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Paper>
                                        </Box>
                                    )}
                                    
                                    {/* Data Summary */}
                                    {tableData.length > 0 && (
                                        <Box sx={{ 
                                            mb: 1.5, 
                                            flexShrink: 0, 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            p: 2,
                                            backgroundColor: colors.backgroundGray,
                                            borderRadius: 2,
                                            border: `1px solid ${colors.border}`
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon sx={{ color: colors.success, mr: 1, fontSize: '20px' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                                                    Validated Excel Data
                                                </Typography>
                                            </Box>
                                            <Chip 
                                                label={`${tableData.length} items`}
                                                color="primary"
                                                size="small"
                                                sx={{ backgroundColor: colors.success, color: 'white' }}
                                            />
                                        </Box>
                                    )}
                                    
                                    {/* Scrollable Table */}
                                    <TableContainer 
                                        component={Paper} 
                                        elevation={0} 
                                        sx={{ 
                                            ...modernCardStyle,
                                            flex: 1,
                                            overflow: 'auto',
                                            maxHeight: '100%',
                                            background: colors.backgroundCard
                                        }}
                                    >
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow sx={{ '& .MuiTableCell-root': {
                                                    background: colors.backgroundAccent,
                                                    fontWeight: 700, 
                                                    position: 'sticky', 
                                                    top: 0, 
                                                    zIndex: 1,
                                                    color: colors.textPrimary,
                                                    borderBottom: `2px solid ${colors.primary}`,
                                                    py: 2,
                                                    textAlign: 'center'
                                                } }}>
                                                    <TableCell align="center" sx={{ width: 80 }}>S. No.</TableCell>
                                                    <TableCell align="center">Item Code</TableCell>
                                                    <TableCell align="center">Qualify Qty</TableCell>
                                                    <TableCell align="center">Rate</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {tableData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center" sx={{ p: 4, color: colors.textSecondary }}>
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <RuleIcon sx={{ fontSize: 40, color: colors.textTertiary, mb: 1 }} />
                                                                <Typography variant="body1" sx={{ fontWeight: 500, color: colors.textSecondary }}>
                                                                    No validated data to display
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: colors.textTertiary, mt: 0.5 }}>
                                                                    Import an Excel file and click "Validate" to see data here
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    tableData.map((row, index) => (
                                                        <TableRow key={index} hover sx={{'&:hover': {backgroundColor: 'rgba(230,230,230,0.4)'}}}>
                                                            <TableCell align="center" sx={{ fontWeight: 500, color: colors.textSecondary }}>
                                                                {index + 1}
                                                            </TableCell>
                                                            <TableCell align="center">{row.ItemCode}</TableCell>
                                                            <TableCell align="center">{row.QualifyQty}</TableCell>
                                                            <TableCell align="center">{row.Rate.toFixed(2)}</TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Container>
                
                <CustomerSearchModal 
                    open={showCustomerSearchModal}
                    onClose={() => setShowCustomerSearchModal(false)}
                    searchResults={searchResults}
                    selectedCustomers={selectedCustomers}
                    handleCustomerSelect={handleCustomerSelect}
                    handleConfirmCustomerSelection={handleConfirmCustomerSelection}
                />
                <MappingProgressDialog 
                    open={showMappingProgressDialog}
                    loadingMapping={loadingMapping}
                    onClose={() => {setShowMappingProgressDialog(false); if(mappingProgress === 100) resetAllState();}}
                    mappingProgress={mappingProgress}
                    mappingStatus={mappingStatus}
                    totalCustomersToUpload={totalCustomersToUpload}
                    currentCustomerUploadIndex={currentCustomerUploadIndex}
                    uploadResults={uploadResults}
                />
                <CustomerMappingConfirmDialog
                    open={showMappingConfirmDialog}
                    onClose={handleCancelMapping}
                    customers={customersToMap}
                    areaName={selectedAreaName}
                    onConfirm={handleConfirmMapping}
                />
                <ValidationProgressDialog
                    open={showValidationProgress}
                    validationProgress={validationProgress}
                    validationStatus={validationStatus}
                    totalCustomers={selectedCustomers.length}
                    onClose={() => setShowValidationProgress(false)}
                />
                <MapSchemeModal
                    open={mapSchemeModalOpen}
                    onClose={handleCloseMapSchemeModal}
                    customer={selectedCustomerForMapping}
                    availableSchemes={availableRebateSchemes}
                    selectedScheme={selectedMappingScheme}
                    onSchemeSelect={handleSchemeSelection}
                    schemeRateData={schemeRateData}
                    loadingSchemes={loadingAvailableSchemes}
                    loadingRateData={loadingSchemeRateData}
                    onMapScheme={handleMapSchemeConfirm}
                />
                <MasterCustomerMappingDialog
                    open={showMasterMappingDialog}
                    onClose={() => setShowMasterMappingDialog(false)}
                    unmappedCustomers={masterUnmappedCustomers}
                    onMapCustomer={handleMasterCustomerMapping}
                    onBulkMapping={handleBulkMasterMapping}
                />
                <DeleteProgressDialog
                    open={showDeleteProgress}
                    deleteProgress={deleteProgress}
                    deleteStatus={deleteStatus}
                    totalCustomers={selectedCustomersForDelete.length}
                    deleteResults={deleteResults}
                    onClose={() => setShowDeleteProgress(false)}
                />

                {/* --- Self-Contained Toast Component --- */}
                <Slide direction="up" in={toast.open} mountOnEnter unmountOnExit>
                    <Alert
                        severity={toast.severity}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            minWidth: 300,
                            zIndex: 1400,
                            borderRadius: '8px',
                            alignItems: 'center',
                            ...glassmorphismStyle,
                            border: `1px solid ${colors[toast.severity] || colors.border}`
                        }}
                        action={
                            <IconButton size="small" color="inherit" onClick={handleCloseToast}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        }
                        iconMapping={{
                            success: <CheckCircleIcon fontSize="inherit" sx={{color: colors.success}} />,
                            error: <CloseIcon fontSize="inherit" sx={{color: colors.error}} />,
                            warning: <CloseIcon fontSize="inherit" sx={{color: colors.warning}} />,
                            info: <CloseIcon fontSize="inherit" sx={{color: colors.primary}} />,
                        }}
                    >
                        {toast.message}
                    </Alert>
                </Slide>
            </Box>

            {/* Central API Chain Processing Loader */}
            {isApiChainProcessing && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                        backdropFilter: 'blur(8px)',
                    }}
                >
                    <Paper
                        elevation={10}
                        sx={{
                            p: 4,
                            borderRadius: '16px',
                            background: colors.backgroundCard,
                            border: `2px solid ${colors.primary}`,
                            maxWidth: '500px',
                            textAlign: 'center',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.02)' },
                                '100%': { transform: 'scale(1)' }
                            }
                        }}
                    >
                        <CircularProgress
                            size={60}
                            sx={{
                                color: colors.primary,
                                mb: 3
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: colors.primary,
                                fontWeight: 'bold',
                                mb: 2
                            }}
                        >
                            Processing ....
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: colors.textSecondary,
                                mb: 3,
                                lineHeight: 1.6
                            }}
                        >
                            { 'Please wait while we process your request ...'}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: colors.error,
                                fontWeight: '600',
                                background: `${colors.error}15`,
                                p: 1.5,
                                borderRadius: '8px',
                                border: `1px solid ${colors.error}30`
                            }}
                        >
                            ⚠️ Please do not close this window or navigate away
                        </Typography>
                    </Paper>
                </Box>
            )}
        </>
    );
};

export default UploadRateCustomerLookup;
